// v10/src/App.js
import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AircraftSelector from './components/AircraftSelector';
import ChecklistDisplay from './components/ChecklistDisplay';
import VoiceAssistant from './components/VoiceAssistant';
import PreflightLog from './components/PreflightLog';
import SkipReminder from './components/SkipReminder';
import WeatherInfo from './components/WeatherInfo';
import ChecklistEditor from './components/ChecklistEditor';
import ReferenceImageModal from './components/ReferenceImageModal';
import CompletionScreen from './components/CompletionScreen';
import MyProfile from './components/MyProfile';
import AboutPage from './components/AboutPage';
import VoiceSelector from './components/VoiceSelector';
import ReviewModal from './components/ReviewModal';
import AdminPanel from './components/admin/AdminPanel';
import OnboardingSlides from './components/OnboardingSlides';
import { clearSeenTips } from './components/SectionTip';
import DesktopBackground from './components/DesktopBackground';
import { defaultSession } from './mock/aircraftData';
import { speakText, stopSpeech, fetchAndStoreManifest } from './utils/speechHelper';
import { usePermissions } from './utils/roles';
import AuthScreen from './components/AuthScreen';
import PilotHistory from './components/PilotHistory';
import PilotFlightLog from './components/PilotFlightLog';
import PilotTurnos from './components/pilot/PilotTurnos';
import WBCalculator from './components/wb/WBCalculator';
import NavigationPlanner from './components/nav/NavigationPlanner';
import PreLandingChecklist from './components/PreLandingChecklist';
import AnimatedSplash from './components/AnimatedSplash';
import NotificationManager from './components/NotificationManager';
import { authService, scheduleExpiryWarning, cancelExpiryWarning, storeTokens } from './services/apiService';
import api, { inspectionService, aircraftService } from './services/apiService';
import ObservationModal from './components/ObservationModal';
import useWakeLock from './hooks/useWakeLock';
import useBadge from './hooks/useBadge';
import TourGuide from './components/TourGuide';
import SuperAdminPanel from './components/SuperAdminPanel';
import { syncTokenToIDB } from './hooks/useOfflineQueue';
import MaintenanceBanner from './components/MaintenanceBanner';
import UpsellModal from './components/UpsellModal';
import PaymentResult from './components/PaymentResult';
import FlowScreen from './components/FlowScreen';
import EmergencyTrainer from './components/EmergencyTrainer';
import PostFlightModal from './components/PostFlightModal';
import QuickOpenModal from './components/QuickOpenModal';
import ModeChoiceModal from './components/ModeChoiceModal';
import { reportsService } from './services/reportsService';
import usePendingHobbs from './hooks/usePendingHobbs';
import { hasFeature, getLimit } from './utils/planFeatures';
import HelpButton from './components/HelpButton';
import useIsMobile from './hooks/useIsMobile';
import { transformChecklistFromBackend, getPhaseProgress } from './utils/checklist';
import { shouldRedirectToDemoHost, DEMO_HOST_REDIRECT_TARGET } from './utils/demoRedirect';

// Alto reservado arriba en mobile cuando la cinta "Modo demo" (DemoBanner)
// está fija arriba, para que no tape los botones fixed anclados a top:16px
// (panel_admin, campanita, menú). Mismo valor usado en AdminPanel.js
// (demoTopOffset) para su propio header — si cambia el alto del ribbon,
// actualizar ambos.
const DEMO_MOBILE_TOP_OFFSET = 52;

// Alto reservado arriba en el selector de aeronaves (mobile + demo): además
// de la cinta (DEMO_MOBILE_TOP_OFFSET), hay que despejar la fila de botones
// fixed (panel_admin/campanita/menú, ~44px) que vive a esa misma altura, más
// un margen. El selector NO puede reservar esto con padding-top a secas
// porque su contenedor centra verticalmente (items-center): al seleccionar
// un avión el card crece y el centrado lo empuja hacia arriba, metiendo el
// header bajo la cinta. Por eso el selector usa este offset con
// alignItems:'flex-start' en vez de center — el header queda anclado a una
// posición fija que no compite con la cinta sin importar cuánto crezca el
// contenido de abajo.
const DEMO_SELECTOR_MOBILE_TOP_OFFSET = DEMO_MOBILE_TOP_OFFSET + 56;

// ─── Session helpers ──────────────────────────────────────────────
const SESSION_KEY           = 'condor360_checklist_session';
const saveChecklistSession  = (d) => { try { localStorage.setItem(SESSION_KEY, JSON.stringify(d)); } catch (_) {} };
const loadChecklistSession  = ()  => { try { const r = localStorage.getItem(SESSION_KEY); return r ? JSON.parse(r) : null; } catch (_) { return null; } };
const clearChecklistSession = ()  => { try { localStorage.removeItem(SESSION_KEY); } catch (_) {} };

const shouldShowSplash = () =>
  !localStorage.getItem('condor360_current_user') &&
  !sessionStorage.getItem('condor360_current_user') &&
  !sessionStorage.getItem('condor360_splash_shown') &&
  !localStorage.getItem('condor360_splash_shown') &&
  !new URLSearchParams(window.location.search).get('reset_token') &&
  !new URLSearchParams(window.location.search).get('demo');

const ONBOARDING_KEY = 'condor360_onboarding_done';

// ── Impersonation banner (draggable) ──────────────────────────────
const ImpersonationBar = ({ orgName, onLogout, t }) => {
  const DRAG_THRESHOLD_PX = 5;
  const barRef = React.useRef(null);
  const dragState = React.useRef({
    active: false, moved: false, pointerId: null,
    offsetX: 0, offsetY: 0, startX: 0, startY: 0,
  });
  const [pos, setPos] = React.useState(null);

  const startDrag = (e) => {
    if (e.target && e.target.closest && e.target.closest('button')) return;
    const bar = barRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    dragState.current = {
      active: true,
      moved: false,
      pointerId: e.pointerId,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      startX: e.clientX,
      startY: e.clientY,
    };
    if (pos == null) setPos({ left: rect.left, top: rect.top });
    try { bar.setPointerCapture(e.pointerId); } catch (_) {}
  };

  const onMove = (e) => {
    const s = dragState.current;
    if (!s.active || e.pointerId !== s.pointerId) return;
    const bar = barRef.current;
    if (!bar) return;
    if (!s.moved) {
      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;
      if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
      s.moved = true;
    }
    const w = bar.offsetWidth;
    const h = bar.offsetHeight;
    let left = e.clientX - s.offsetX;
    let top  = e.clientY - s.offsetY;
    const maxLeft = window.innerWidth - w;
    const maxTop  = window.innerHeight - h;
    if (left < 0) left = 0; else if (left > maxLeft) left = maxLeft;
    if (top  < 0) top  = 0; else if (top  > maxTop)  top  = maxTop;
    setPos({ left, top });
  };

  const endDrag = (e) => {
    const s = dragState.current;
    if (!s.active) return;
    const bar = barRef.current;
    try { bar && bar.releasePointerCapture(e.pointerId); } catch (_) {}
    s.active = false;
    s.moved = false;
  };

  const handleLogoutClick = () => { onLogout(); };
  const stopBtnPointer = (e) => { e.stopPropagation(); };

  const baseStyle = {
    position: 'fixed', zIndex: 200,
    background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white',
    padding: '10px 20px', borderRadius: 20, fontSize: 13, fontWeight: 700,
    boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
    display: 'flex', alignItems: 'center', gap: 8,
    cursor: dragState.current.active ? 'grabbing' : 'grab',
    touchAction: 'none', userSelect: 'none',
  };
  const posStyle = pos
    ? { left: pos.left, top: pos.top }
    : { bottom: 20, left: '50%', transform: 'translateX(-50%)' };

  return (
    <div
      ref={barRef}
      style={{ ...baseStyle, ...posStyle }}
      onPointerDown={startDrag}
      onPointerMove={onMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <span>🛡️ {t('support_banner', { name: orgName })}</span>
      <button onClick={handleLogoutClick} onPointerDown={stopBtnPointer}
        style={{ background:'rgba(255,255,255,0.2)', border:'none', borderRadius:10, padding:'4px 10px',
          color:'white', fontWeight:700, fontSize:12, cursor:'pointer', touchAction:'manipulation' }}>
        ✕ {t('logout')}
      </button>
    </div>
  );
};

// ── Demo mode banner ─────────────────────────────────────────────
// Visible sólo cuando el user tiene is_demo=true. El toggle admin↔piloto
// reemite el token vía /auth/demo-login con el otro rol.
//
// Desktop: pill flotante fixed abajo-centro (sin cambios, no reportado como roto).
// Mobile: el mismo pill fixed abajo tapaba CTAs (botón "Iniciar chequeo" en el
// selector, "Cerrar sesión"/"Acerca de" al pie del menú admin) en TODAS las
// pantallas donde vive contenido interactivo abajo — no escala compensar con
// padding pantalla por pantalla. En mobile se reubica arriba como cinta fina
// fixed (DEMO_MOBILE_TOP_OFFSET en App.js / demoTopOffset en AdminPanel.js
// reservan el espacio una sola vez, en los pocos elementos fixed anclados
// arriba, en vez de en cada CTA de abajo).
const DemoBanner = ({ currentUser, onSwitchView, switching, isMobile }) => {
  const { t } = useTranslation('auth');
  const role = currentUser?.role;
  const otherRole = role === 'admin' ? 'pilot' : 'admin';
  const toggleLabel = otherRole === 'pilot'
    ? t('demo_view_as_pilot')
    : t('demo_view_as_admin');

  if (isMobile) {
    return (
      <div
        style={{
          // zIndex bajo a propósito: en la pantalla Selector, el contenido
          // vive envuelto en un div con zIndex:1 (App.js, selector de
          // aeronaves) que crea su propio stacking context — cualquier modal
          // fixed/inset:0 montado ahí adentro (ej. NotificationManager) queda
          // "atrapado" en esa capa 1 aunque su propio zIndex interno sea más
          // alto. El banner tiene que quedar por debajo de esa capa 1 (no
          // solo por debajo del zIndex nominal del modal) para que cualquier
          // overlay que se abra le gane siempre. El contenido normal de la
          // pantalla no se ve afectado porque va empujado hacia abajo por
          // DEMO_MOBILE_TOP_OFFSET, así que nunca compite visualmente con
          // esta cinta.
          position: 'fixed', zIndex: 0,
          top: 0, left: 0, right: 0,
          background: 'linear-gradient(135deg,#0ea5e9,#6366f1)',
          color: 'white',
          padding: 'calc(env(safe-area-inset-top, 0px) + 6px) 10px 6px',
          fontSize: 12, fontWeight: 700,
          boxShadow: '0 2px 12px rgba(99,102,241,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          maxWidth: '150px',
        }}>
          <span style={{ fontSize: 13, lineHeight: 1 }}>🎬</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('demo_banner')}</span>
        </span>
        <button
          onClick={onSwitchView}
          disabled={switching}
          style={{
            background: 'rgba(255,255,255,0.22)', border: 'none', borderRadius: 999,
            padding: '4px 10px', color: 'white', fontWeight: 700, fontSize: 11,
            cursor: switching ? 'wait' : 'pointer', touchAction: 'manipulation',
            fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          {switching ? t('demo_switching_view') : toggleLabel}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed', zIndex: 200,
        left: '50%', transform: 'translateX(-50%)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        background: 'linear-gradient(135deg,#0ea5e9,#6366f1)',
        color: 'white',
        padding: '8px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700,
        boxShadow: '0 6px 22px rgba(99,102,241,0.45)',
        display: 'flex', alignItems: 'center', gap: 8,
        maxWidth: 'calc(100vw - 24px)', flexWrap: 'wrap', justifyContent: 'center',
        rowGap: 6,
      }}
    >
      <span style={{ display:'inline-flex', alignItems:'center', gap:6, whiteSpace:'nowrap' }}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>🎬</span>
        <span>{t('demo_banner')}</span>
      </span>
      <button
        onClick={onSwitchView}
        disabled={switching}
        style={{
          background: 'rgba(255,255,255,0.22)', border: 'none', borderRadius: 999,
          padding: '5px 12px', color: 'white', fontWeight: 700, fontSize: 12,
          cursor: switching ? 'wait' : 'pointer', touchAction: 'manipulation',
          fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}
      >
        {switching ? t('demo_switching_view') : toggleLabel}
      </button>
    </div>
  );
};

const getGreeting = (name, t) => {
  const hour = new Date().getHours();
  const saludo = hour >= 6 && hour < 12 ? t('good_morning')
               : hour >= 12 && hour < 20 ? t('good_afternoon')
               : t('good_evening');
  const firstName = name ? name.split(' ')[0] : t('pilot');
  return `${saludo}, ${firstName}.`;
};

// ── Selector Menu (mobile-friendly) ──────────────────────────────
const SelectorMenu = ({ permissions, darkMode, onAdmin, onOnboarding, onFlightLog, onPilotTurnos, onWBCalculator, onNavigationPlanner, onLogout, onProfile, onAbout, onRegisterFlightManual, orgFeatures, onUpsell, currentUser }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const handleDownloadManual = async () => {
    try {
      const lang = (i18n?.language || 'es').startsWith('en') ? 'en' : 'es';
      await reportsService.manual(lang);
    } catch (err) {
      console.error('Error descargando el manual:', err);
      alert(t('manual_download_error', { defaultValue: 'No se pudo descargar el manual. Reintentá en unos segundos.' }));
    }
  };
  const ref = React.useRef(null);
  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('touchstart', h); };
  }, []);

  const items = [
    ...(permissions.canAccessPanel ? [{ icon: '🛡️', label: t('panel_admin'), onClick: onAdmin }] : []),
    ...(orgFeatures?.flight_log !== false
      ? [{ icon: '✈️', label: t('my_flights'), onClick: onFlightLog }]
      : [{ icon: '🔒', label: t('my_flights'), onClick: () => onUpsell && onUpsell('flight_log'), locked: true }]),
    ...(onRegisterFlightManual
      ? [{ icon: '📝', label: t('register_flight_manual') || 'Registrar vuelo', onClick: onRegisterFlightManual }]
      : []),
    { icon: '📅', label: t('my_schedule'),     onClick: onPilotTurnos },
    ...(hasFeature(currentUser, 'wb')
      ? [{ icon: '⚖️', label: t('wb_calculator', { ns: 'admin' }), onClick: onWBCalculator }]
      : []),
    ...(hasFeature(currentUser, 'navigation')
      ? [{ icon: '🧭', label: t('nav_planner', { ns: 'admin' }), onClick: onNavigationPlanner }]
      : []),
    { icon: '👤', label: t('my_profile'),      onClick: onProfile },
    { icon: '🗺️', label: t('interactive_tour'), onClick: onOnboarding },
    { icon: '📖', label: t('pdf_manual'),      onClick: handleDownloadManual },
    { icon: 'ℹ️', label: t('about_menu_item'), onClick: onAbout },
    { icon: '🚪', label: t('logout'),          onClick: onLogout, danger: true },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <motion.button onClick={() => setOpen(p => !p)} whileTap={{ scale: 0.93 }}
        style={{ width:'40px', height:'40px', borderRadius:'12px', background:'rgba(255,255,255,0.9)', backdropFilter:'blur(14px)', border:'1px solid rgba(59,130,246,0.15)', boxShadow:'0 4px 20px rgba(59,130,246,0.1)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'4px', cursor:'pointer' }}>
        {[0,1,2].map(i => (
          <motion.div key={i}
            animate={open ? i===0?{rotate:45,y:6}:i===1?{opacity:0,scaleX:0}:{rotate:-45,y:-6} : {rotate:0,y:0,opacity:1,scaleX:1}}
            transition={{ duration: 0.2 }}
            style={{ width:'18px', height:'2px', borderRadius:'2px', background:'rgba(30,40,80,0.65)' }} />
        ))}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity:0,scale:0.92,y:-8 }} animate={{ opacity:1,scale:1,y:0 }} exit={{ opacity:0,scale:0.92,y:-8 }} transition={{ duration:0.15 }}
            style={{ position:'absolute', top:'48px', right:0, zIndex:100, background: darkMode?'rgba(12,17,30,0.98)':'rgba(255,255,255,0.98)', backdropFilter:'blur(24px)', border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(59,130,246,0.12)', borderRadius:'18px', padding:'6px', boxShadow: darkMode?'0 20px 50px rgba(0,0,0,0.55)':'0 8px 36px rgba(0,0,0,0.13)', minWidth:'180px' }}>
            {items.map((item, idx) => (
              <React.Fragment key={idx}>
                {item.danger && <div style={{ height:'1px', margin:'4px 8px', background: darkMode?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.06)' }} />}
                <motion.button onClick={() => { item.onClick(); setOpen(false); }} whileTap={{ scale:0.97 }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'11px 14px', borderRadius:'11px', border:'none', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontSize:'14px', fontWeight:600, textAlign:'left', color: item.danger?'#ef4444': darkMode?'rgba(255,255,255,0.85)':'rgba(15,25,55,0.85)', transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = item.danger?'rgba(239,68,68,0.09)': darkMode?'rgba(255,255,255,0.06)':'rgba(59,130,246,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize:'16px', width:'22px', textAlign:'center' }}>{item.icon}</span>
                  {item.label}
                </motion.button>
              </React.Fragment>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Hamburger Menu ────────────────────────────────────────────────
const HeaderMenu = ({ permissions, darkMode, onAdmin, onBack, onHistory, onEdit, onHelp, onAbout, onLogout }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('touchstart', h); };
  }, []);

  const items = [
    ...(permissions.canAccessPanel ? [{ icon: '🛡️', label: t('panel_admin'), onClick: onAdmin }] : []),
    { icon: '←',  label: t('back'),           onClick: onBack    },
    { icon: '📋', label: t('history'),         onClick: onHistory },
    ...(permissions.can('editChecklist') ? [{ icon: '⚙️', label: t('edit_checklist'), onClick: onEdit }] : []),
    { icon: '🗺️', label: t('interactive_tour'), onClick: onHelp  },
    { icon: 'ℹ️', label: t('about_menu_item'), onClick: onAbout },
    { icon: '🚪', label: t('logout'),          onClick: onLogout, danger: true },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <motion.button onClick={() => setOpen(p => !p)} whileTap={{ scale: 0.93 }}
        style={{ width:'40px', height:'40px', borderRadius:'12px', background: darkMode?'rgba(20,25,40,0.95)':'rgba(255,255,255,0.95)', backdropFilter:'blur(14px)', border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(59,130,246,0.15)', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.4)':'0 4px 20px rgba(59,130,246,0.12)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'4px', cursor:'pointer' }}>
        {[0,1,2].map(i => (
          <motion.div key={i}
            animate={open ? i===0?{rotate:45,y:6}:i===1?{opacity:0,scaleX:0}:{rotate:-45,y:-6} : {rotate:0,y:0,opacity:1,scaleX:1}}
            transition={{ duration: 0.2 }}
            style={{ width:'18px', height:'2px', borderRadius:'2px', background: darkMode?'rgba(255,255,255,0.75)':'rgba(30,40,80,0.65)' }} />
        ))}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity:0,scale:0.92,y:-8 }} animate={{ opacity:1,scale:1,y:0 }} exit={{ opacity:0,scale:0.92,y:-8 }} transition={{ duration:0.15 }}
            style={{ position:'absolute', top:'48px', right:0, zIndex:100, background: darkMode?'rgba(12,17,30,0.98)':'rgba(255,255,255,0.98)', backdropFilter:'blur(24px)', border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(59,130,246,0.12)', borderRadius:'18px', padding:'6px', boxShadow: darkMode?'0 20px 50px rgba(0,0,0,0.55)':'0 8px 36px rgba(0,0,0,0.13)', minWidth:'190px' }}>
            {items.map((item, idx) => (
              <React.Fragment key={idx}>
                {item.danger && <div style={{ height:'1px', margin:'4px 8px', background: darkMode?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.06)' }} />}
                <motion.button onClick={() => { item.onClick(); setOpen(false); }} whileTap={{ scale:0.97 }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'11px 14px', borderRadius:'11px', border:'none', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontSize:'14px', fontWeight:600, textAlign:'left', color: item.danger?'#ef4444': darkMode?'rgba(255,255,255,0.85)':'rgba(15,25,55,0.85)', transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = item.danger?'rgba(239,68,68,0.09)': darkMode?'rgba(255,255,255,0.06)':'rgba(59,130,246,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize:'16px', width:'22px', textAlign:'center' }}>{item.icon}</span>
                  {item.label}
                </motion.button>
              </React.Fragment>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Session Expiry Warning Modal ──────────────────────────────────
const SessionWarningModal = ({ darkMode, onRenew, onLogout }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position:'fixed', inset:0, zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)' }}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        style={{ width:'100%', maxWidth:'360px', borderRadius:'24px', overflow:'hidden', background: darkMode?'rgba(17,24,39,1)':'white', boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}
      >
        <div style={{ height:'4px', background:'linear-gradient(90deg,#f59e0b,#ef4444)' }} />
        <div style={{ padding:'24px' }}>
          <div style={{ fontSize:'40px', textAlign:'center', marginBottom:'12px' }}>⏱️</div>
          <h3 style={{ margin:'0 0 8px', textAlign:'center', fontSize:'18px', fontWeight:800, color: darkMode?'white':'#111827' }}>
            {t('session_expiring_title')}
          </h3>
          <p style={{ margin:'0 0 20px', textAlign:'center', fontSize:'14px', color: darkMode?'rgba(156,163,175,1)':'rgba(107,114,128,1)' }}>
            {t('session_expiring_desc')}
          </p>
          <div style={{ display:'flex', gap:'10px' }}>
            <button onClick={onRenew}
              style={{ flex:1, padding:'12px', borderRadius:'14px', border:'none', cursor:'pointer', background:'linear-gradient(135deg,#2563eb,#7c3aed)', color:'white', fontWeight:700, fontSize:'14px', fontFamily:'inherit' }}>
              {t('continue_session')}
            </button>
            <button onClick={onLogout}
              style={{ padding:'12px 16px', borderRadius:'14px', border:`1px solid ${darkMode?'rgba(75,85,99,1)':'rgba(229,231,235,1)'}`, cursor:'pointer', background:'transparent', color: darkMode?'rgba(156,163,175,1)':'rgba(107,114,128,1)', fontWeight:600, fontSize:'14px', fontFamily:'inherit' }}>
              {t('logout')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Demo-blocked modal ────────────────────────────────────────────
// Aparece cuando el backend rechaza una acción sensible con 403 + demo:true.
// Reemplaza el error genérico por un cartel amable con CTA comercial. El
// evento lo dispara el response interceptor en apiService.js.
const DemoBlockedModal = ({ darkMode, message, onClose }) => {
  const { t } = useTranslation('auth');
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position:'fixed', inset:0, zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:'rgba(0,0,0,0.55)', backdropFilter:'blur(6px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        style={{ width:'100%', maxWidth:'380px', borderRadius:'24px', overflow:'hidden', background: darkMode?'rgba(17,24,39,1)':'white', boxShadow:'0 24px 60px rgba(0,0,0,0.3)' }}
      >
        <div style={{ height:'4px', background:'linear-gradient(90deg,#0ea5e9,#6366f1)' }} />
        <div style={{ padding:'28px 24px 24px' }}>
          <div style={{ fontSize:'44px', textAlign:'center', marginBottom:'10px' }}>🎬</div>
          <h3 style={{ margin:'0 0 8px', textAlign:'center', fontSize:'18px', fontWeight:800, color: darkMode?'white':'#0f172a' }}>
            {t('demo_blocked_title')}
          </h3>
          <p style={{ margin:'0 0 18px', textAlign:'center', fontSize:'14px', color: darkMode?'rgba(156,163,175,1)':'rgba(107,114,128,1)', lineHeight:1.5 }}>
            {message || t('demo_blocked_body')}
          </p>
          <div style={{ display:'flex', gap:'10px' }}>
            <a
              href="mailto:info@condor360.app?subject=Quiero%20una%20demo%20completa"
              style={{ flex:1, padding:'12px', borderRadius:'14px', textAlign:'center', textDecoration:'none', background:'linear-gradient(135deg,#0ea5e9,#6366f1)', color:'white', fontWeight:700, fontSize:'14px' }}
            >
              {t('demo_blocked_cta')}
            </a>
            <button onClick={onClose}
              style={{ padding:'12px 16px', borderRadius:'14px', border:`1px solid ${darkMode?'rgba(75,85,99,1)':'rgba(229,231,235,1)'}`, cursor:'pointer', background:'transparent', color: darkMode?'rgba(156,163,175,1)':'rgba(107,114,128,1)', fontWeight:600, fontSize:'14px', fontFamily:'inherit' }}>
              {t('demo_blocked_close')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ✅ Modal de confirmación prevuelo ────────────────────────────────
const PreflightReadyModal = ({ darkMode, aircraft, pilotName, onConfirm, tFn }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    style={{ position:'fixed', inset:0, zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:'rgba(0,0,0,0.55)', backdropFilter:'blur(8px)' }}
  >
    <motion.div
      initial={{ scale: 0.9, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 24 }}
      transition={{ type:'spring', stiffness:140, damping:18 }}
      style={{ width:'100%', maxWidth:'380px', borderRadius:'28px', overflow:'hidden', background: darkMode?'rgba(10,15,30,0.97)':'white', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', border: darkMode?'1px solid rgba(99,102,241,0.2)':'1px solid rgba(226,232,240,1)' }}
    >
      <div style={{ height:'4px', background:'linear-gradient(90deg,#2563eb,#7c3aed,#2563eb)', backgroundSize:'200% 100%' }} />
      <div style={{ padding:'32px 28px' }}>
        {pilotName && (
          <p style={{ margin:'0 0 12px', textAlign:'center', fontSize:'15px', fontWeight:700, color: darkMode?'rgba(147,197,253,0.9)':'#2563eb' }}>
            {getGreeting(pilotName, tFn)}
          </p>
        )}
        <motion.div
          animate={{ y:[0,-6,0], rotate:[0,3,-3,0] }}
          transition={{ duration:3, repeat:Infinity, ease:'easeInOut' }}
          style={{ fontSize:'56px', textAlign:'center', marginBottom:'12px' }}
        >✈️</motion.div>
        <p style={{ margin:'0 0 4px', textAlign:'center', fontSize:'12px', fontWeight:700, color: darkMode?'rgba(255,255,255,0.35)':'rgba(100,116,139,1)', textTransform:'uppercase', letterSpacing:'0.1em' }}>
          {aircraft?.manufacturer} {aircraft?.model_name}
          {aircraft?.registration ? ` · ${aircraft.registration}` : ''}
        </p>
        <h3 style={{ margin:'0 0 10px', textAlign:'center', fontSize:'22px', fontWeight:900, color: darkMode?'white':'#0f172a' }}>
          {tFn('ready_for_check')}
        </h3>
        <p style={{ margin:'0 0 28px', textAlign:'center', fontSize:'14px', color: darkMode?'rgba(255,255,255,0.45)':'#64748b', lineHeight:1.6 }}
          dangerouslySetInnerHTML={{ __html: tFn('valentina_guide') }} />
        <motion.button
          onClick={onConfirm}
          whileHover={{ scale:1.02, y:-2 }}
          whileTap={{ scale:0.97 }}
          style={{ width:'100%', padding:'17px', border:'none', borderRadius:'18px', cursor:'pointer', fontFamily:'inherit', fontWeight:800, fontSize:'17px', color:'white', background:'linear-gradient(135deg,#2563eb,#7c3aed)', boxShadow:'0 8px 28px rgba(37,99,235,0.35)', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px' }}
        >
          <span style={{ fontSize:'20px' }}>🛫</span>
          {tFn('start_check')}
        </motion.button>
        <p style={{ margin:'16px 0 0', textAlign:'center', fontSize:'11px', color: darkMode?'rgba(255,255,255,0.2)':'rgba(100,116,139,0.5)', letterSpacing:'0.05em' }}>
          {tFn('position_reminder')}
        </p>
      </div>
    </motion.div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────
const App = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [selectedAircraft, setSelectedAircraft]         = useState(null);
  const [sessionData, setSessionData]                   = useState(defaultSession);
  const [checkedItems, setCheckedItems]                 = useState([]);
  const [skippedItems, setSkippedItems]                 = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex]   = useState(0);
  const [currentItemIndex, setCurrentItemIndex]         = useState(0);
  const [isChecking, setIsChecking]                     = useState(false);
  const [pendingItem, setPendingItem]                   = useState(null);
  const [showSelector, setShowSelector]                 = useState(true);
  const [isChecklistComplete, setIsChecklistComplete]   = useState(false);
  const [voiceEnabled, setVoiceEnabled]                 = useState(true);
  const [justConfirmed, setJustConfirmed]               = useState(false);
  const [pendingReminders, setPendingReminders]         = useState([]);
  const [firstCheckDone, setFirstCheckDone]             = useState(false);
  const [darkMode, setDarkMode]                         = useState(() => {
    const hour = new Date().getHours();
    return hour >= 20 || hour < 6;
  });
  const [reviewingSkipped, setReviewingSkipped]         = useState(false);
  const [skippedReviewQueue, setSkippedReviewQueue]     = useState([]);
  const [showReviewModal, setShowReviewModal]           = useState(false);
  const [reviewIndex, setReviewIndex]                   = useState(0);
  const [customAircrafts, setCustomAircrafts]           = useState([]);
  const [showEditor, setShowEditor]                     = useState(false);
  const [itemPhotos, setItemPhotos]                     = useState({});
  const [referenceModal, setReferenceModal]             = useState(null);
  const [showVoiceSelector, setShowVoiceSelector]       = useState(false);
  const phrases = i18n.language === 'en'
    ? ["Let's continue with...", "Moving on to...", "Next up..."]
    : i18n.language === 'es'
    ? ["Continuemos con...", "Sigamos con...", "Ahora vamos con..."]
    : ["Continuemos con...", "Sigamos por...", "Ahora vamos con..."];
  const [phraseIndex, setPhraseIndex]                   = useState(0);
  const [currentUser, setCurrentUser]                   = useState(null);
  const [isAuthenticated, setIsAuthenticated]           = useState(false);
  const [demoSwitching, setDemoSwitching]               = useState(false);
  // Auto-login de demo por ?demo=1 desde la landing. Ver useEffect más abajo.
  const [demoBooting, setDemoBooting]                   = useState(
    () => !!new URLSearchParams(window.location.search).get('demo')
          && !localStorage.getItem('condor360_current_user')
          && !sessionStorage.getItem('condor360_current_user')
  );
  const [demoBootError, setDemoBootError]               = useState(null);
  const [isAppLoading, setIsAppLoading]                 = useState(shouldShowSplash);
  const [showAdminPanel, setShowAdminPanel]             = useState(false);
  const [showHistory, setShowHistory]                   = useState(false);
  const [showFlightLog, setShowFlightLog]               = useState(false);
  const [showPilotTurnos, setShowPilotTurnos]           = useState(false);
  const [showWBCalculator, setShowWBCalculator]         = useState(false);
  const [showNavigationPlanner, setShowNavigationPlanner] = useState(false);
  const [showPreLanding, setShowPreLanding]             = useState(false);
  const [preLandingAircraft, setPreLandingAircraft]     = useState(null);
  const [showFlowMode, setShowFlowMode] = useState(() => {
    try { return sessionStorage.getItem('condor360_flow_active') === 'true'; } catch { return false; }
  });
  const [flowAircraft, setFlowAircraft] = useState(() => {
    try { const s = sessionStorage.getItem('condor360_flow_aircraft'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [flowInspectionId, setFlowInspectionId] = useState(() => {
    try { return sessionStorage.getItem('condor360_flow_inspection_id') || null; } catch { return null; }
  });
  const [showEmergencyTrainer, setShowEmergencyTrainer] = useState(() => {
    try { return sessionStorage.getItem('condor360_emergency_active') === 'true'; } catch { return false; }
  });
  const [emergencyAircraft, setEmergencyAircraft] = useState(() => {
    try { const s = sessionStorage.getItem('condor360_emergency_aircraft'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [emergencyProcedureId, setEmergencyProcedureId] = useState(() => {
    try { return sessionStorage.getItem('condor360_emergency_proc_id') || null; } catch { return null; }
  });
  const [emergencyProcedures, setEmergencyProcedures]   = useState([]);
  const [showEmergencySelect, setShowEmergencySelect]   = useState(false);
  const [weatherData, setWeatherData]                   = useState(null);
  const [showOnboarding, setShowOnboarding]             = useState(false);
  const [currentSectionName, setCurrentSectionName]     = useState('');
  const [isSpeaking, setIsSpeaking]                     = useState(false);
  const [showSessionWarning, setShowSessionWarning]     = useState(false);
  // Demo gate — cartel comercial cuando el back rechaza con 403 demo:true.
  const [demoBlockedMessage, setDemoBlockedMessage]     = useState(null);
  const [observationItems, setObservationItems]         = useState([]);
  const [pendingObservation, setPendingObservation]     = useState(null);
  const [showTour, setShowTour]                         = useState(false);
  const [showPreflightModal, setShowPreflightModal]     = useState(false);
  const [pendingStartData, setPendingStartData]         = useState(null);
  const [activeInspectionId, setActiveInspectionId]     = useState(null);
  // Multi-fase (Sub-etapa 3.2b) — contenedor y plan de fases del chequeo en
  // curso. Para orgs de una sola fase (ej. Madryn, phasesPlan=['prevuelo']),
  // esto se comporta como un no-op: phasesPlan siempre queda en longitud 1.
  const [groupId, setGroupId]                           = useState(null);
  const [activePhase, setActivePhase]                   = useState('prevuelo');
  const [phasesPlan, setPhasesPlan]                     = useState(['prevuelo']);
  const [phasesDone, setPhasesDone]                     = useState([]);
  // Sub-etapa 3.2c-ii — fases obligatorias (policy de org, required_phases)
  // y fases opcionales que el piloto decidió saltear en ESTE chequeo.
  // requiredPhases NO se toca desde acá salvo al arrancar un chequeo nuevo
  // (handlePreflightConfirm) — es una policy de org, no cambia por fase.
  const [requiredPhases, setRequiredPhases]             = useState(['prevuelo']);
  const [phasesSkipped, setPhasesSkipped]               = useState([]);
  // Sub-etapa 3.2c-iii — snapshots por fase cerrada (checklist + ítems +
  // firma de ESA fase) para armar el PDF consolidado al cierre del
  // contenedor. La fase final (la que está en pantalla al exportar) se
  // agrega en el momento del export, no acá (ver CompletionScreen).
  const [phasesData, setPhasesData]                     = useState([]);
  const [openFlightBlock, setOpenFlightBlock]           = useState(null); // {aircraft_label, recorded_by_name, hobbs_start, ...}
  // Parte 4 — Registro rápido (selección de modalidad)
  // Parte 5 — El AOG override del registro rápido se maneja dentro de
  // QuickOpenModal con su propio AogOverrideModal (mismo patrón que
  // CompletionScreen para Valentina). Ya no necesitamos estados/handlers
  // en App.js para los 409/503 AOG del quick-open.
  const [showModeChoice, setShowModeChoice]             = useState(false);
  const [showQuickOpen, setShowQuickOpen]               = useState(false);
  const [showProfile, setShowProfile]                   = useState(() => sessionStorage.getItem('condor360_showing_profile') === 'true');
  const [showAbout, setShowAbout]                       = useState(false);
  const [upsellFeature, setUpsellFeature]               = useState(null);
  const [paymentResult, setPaymentResult]               = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('payment');
    if (status && ['success', 'failure', 'pending'].includes(status)) {
      return { status, plan: params.get('plan') || '' };
    }
    return null;
  });
  const [impersonatedOrg, setImpersonatedOrg]           = useState(null);

  React.useEffect(() => {
    window.__condor360_impersonate_org_id = impersonatedOrg?.id || null;
    // Refrescar /me al entrar o salir de impersonación para que
    // organization_features / effective_features reflejen la org impersonada
    // (o vuelvan a las del SuperAdmin al salir) sin esperar al polling.
    if (currentUser?.role !== 'superadmin') return;
    authService.me().then(res => {
      const fresh = res?.data?.user;
      if (!fresh?.organization_features) return;
      setCurrentUser(prev => {
        if (!prev) return prev;
        const updated = { ...prev, organization_features: fresh.organization_features };
        if (fresh.effective_features) updated.effective_features = fresh.effective_features;
        try { localStorage.setItem('condor360_current_user', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
    }).catch(() => { /* el polling de 30s lo recupera */ });
    // eslint-disable-next-line
  }, [impersonatedOrg]);

  const permissions  = usePermissions(currentUser);
  const tourTimerRef = React.useRef(null);

  useWakeLock(!!selectedAircraft && isChecking && !isChecklistComplete);
  useBadge();
  const { pendingRecord: pendingHobbs, dismiss: dismissHobbs, complete: completeHobbs, recheck: recheckHobbs } = usePendingHobbs(currentUser);

  const safeSpeak = (text, onDone = () => {}, ctx = {}) => {
    if (voiceEnabled) {
      stopSpeech(); setIsSpeaking(true);
      speakText(text, () => { setIsSpeaking(false); onDone(); }, { aircraftId: selectedAircraft?.id, ...ctx });
    } else { onDone(); }
  };

  const playChime = (onDone = () => {}) => {
    if (!voiceEnabled) { onDone(); return; }
    try {
      const ctx  = new (window.AudioContext || window.webkitAudioContext)();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type      = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
      osc.onended = () => { ctx.close(); onDone(); };
    } catch { onDone(); }
  };

  const openRefModal  = (item) => { setReferenceModal(null); requestAnimationFrame(() => setTimeout(() => setReferenceModal(item), 80)); };
  const closeRefModal = () => setReferenceModal(null);
  const handleSplashComplete = () => { sessionStorage.setItem('condor360_splash_shown','true'); localStorage.setItem('condor360_splash_shown','true'); setIsAppLoading(false); };

  useEffect(() => {
    const onExpired      = () => handleLogout();
    const onExpiringSoon = async () => {
      try { await authService.me(); scheduleExpiryWarning(900); }
      catch { setShowSessionWarning(true); }
    };
    const onDemoBlocked = (e) => {
      setDemoBlockedMessage(e?.detail?.message || '');
    };
    window.addEventListener('condor360:session-expired',       onExpired);
    window.addEventListener('condor360:session-expiring-soon', onExpiringSoon);
    window.addEventListener('condor360:demo-blocked',          onDemoBlocked);

    const flagsInterval = setInterval(async () => {
      try {
        const res = await authService.me();
        if (res?.data?.user?.organization_features) {
          const freshFeatures   = res.data.user.organization_features;
          const freshEffective  = res.data.user.effective_features;
          setCurrentUser(prev => {
            if (!prev) return prev;
            const currentFlags     = JSON.stringify(prev.organization_features || {});
            const newFlags         = JSON.stringify(freshFeatures);
            const currentEffective = JSON.stringify(prev.effective_features || {});
            const newEffective     = JSON.stringify(freshEffective || {});
            if (currentFlags === newFlags && currentEffective === newEffective) return prev;
            const updated = { ...prev, organization_features: freshFeatures };
            if (freshEffective) updated.effective_features = freshEffective;
            try { localStorage.setItem('condor360_current_user', JSON.stringify(updated)); } catch (_) {}
            return updated;
          });
        }
      } catch (err) {
        // Telemetría: no cambiar comportamiento (la app sigue), pero
        // reportar a Sentry para medir tasa real de fallo del polling /me.
        try {
          let hasEffective = 'unknown';
          try {
            const stored = localStorage.getItem('condor360_current_user');
            if (stored) {
              const u = JSON.parse(stored);
              hasEffective = u?.effective_features ? 'yes' : 'no';
            }
          } catch (_) { /* no-op */ }
          Sentry.captureException(err, {
            tags: { area: 'auth_me', phase: 'poll', has_effective_features: hasEffective },
          });
        } catch (_) { /* no-op */ }
      }
    }, 30 * 1000);

    return () => {
      window.removeEventListener('condor360:session-expired',       onExpired);
      window.removeEventListener('condor360:session-expiring-soon', onExpiringSoon);
      window.removeEventListener('condor360:demo-blocked',          onDemoBlocked);
      clearInterval(flagsInterval);
    };
  // eslint-disable-next-line
  }, []);

  const [qrAircraftId, setQrAircraftId] = React.useState(() => {
    return new URLSearchParams(window.location.search).get('aircraft') || null;
  });

  const applyInitialView = (user) => {
    const perms = usePermissions(user);
    const saved = loadChecklistSession();
    if (saved?.aircraft && !saved?.isComplete) {
      setSelectedAircraft(saved.aircraft); setSessionData(saved.sessionData || defaultSession);
      setCheckedItems(saved.checkedItems || []); setSkippedItems(saved.skippedItems || []);
      setCurrentSectionIndex(saved.currentSectionIndex || 0); setCurrentItemIndex(saved.currentItemIndex || 0);
      setPendingReminders(saved.pendingReminders || []);
      setObservationItems(saved.observationItems || []);
      setIsChecklistComplete(false); setIsChecking(true); setShowSelector(false); setShowAdminPanel(false);
      if (saved.activeInspectionId) setActiveInspectionId(saved.activeInspectionId);
      // Multi-fase (Sub-etapa 3.2b) — patrón defensivo `if (saved.x)`: una
      // sesión guardada ANTES de este cambio no trae estos campos, así que
      // no se pisa el estado con `undefined` y se comporta como antes.
      if (saved.groupId)        setGroupId(saved.groupId);
      if (saved.activePhase)    setActivePhase(saved.activePhase);
      if (saved.phasesPlan)     setPhasesPlan(saved.phasesPlan);
      if (saved.phasesDone)     setPhasesDone(saved.phasesDone);
      // Sub-etapa 3.2c-ii — mismo patrón defensivo.
      if (saved.requiredPhases) setRequiredPhases(saved.requiredPhases);
      if (saved.phasesSkipped)  setPhasesSkipped(saved.phasesSkipped);
      // Sub-etapa 3.2c-iii — mismo patrón defensivo.
      if (saved.phasesData)     setPhasesData(saved.phasesData);
      const sec = saved.aircraft.checklist?.[saved.currentSectionIndex || 0];
      if (sec) setCurrentSectionName(sec.section);
      fetchAndStoreManifest(saved.aircraft.id);
    } else if (saved?.isComplete) {
      setSelectedAircraft(saved.aircraft); setCheckedItems(saved.checkedItems || []);
      setSkippedItems(saved.skippedItems || []); setPendingReminders(saved.pendingReminders || []);
      setSessionData(saved.sessionData || defaultSession);
      setObservationItems(saved.observationItems || []);
      setIsChecklistComplete(true); setShowSelector(false); setShowAdminPanel(false);
      // Multi-fase (Sub-etapa 3.2b) — mismo patrón defensivo que arriba. Al
      // restaurar acá, CompletionScreen recalcula isLastPhase con estos
      // valores: si el piloto cerró la app parado en "Continuar con [fase]",
      // vuelve a esa misma pantalla (no hace falta un tercer caso aparte).
      if (saved.activeInspectionId) setActiveInspectionId(saved.activeInspectionId);
      if (saved.groupId)        setGroupId(saved.groupId);
      if (saved.activePhase)    setActivePhase(saved.activePhase);
      if (saved.phasesPlan)     setPhasesPlan(saved.phasesPlan);
      if (saved.phasesDone)     setPhasesDone(saved.phasesDone);
      // Sub-etapa 3.2c-ii — mismo patrón defensivo.
      if (saved.requiredPhases) setRequiredPhases(saved.requiredPhases);
      if (saved.phasesSkipped)  setPhasesSkipped(saved.phasesSkipped);
      // Sub-etapa 3.2c-iii — mismo patrón defensivo.
      if (saved.phasesData)     setPhasesData(saved.phasesData);
      fetchAndStoreManifest(saved.aircraft.id);
    } else if (sessionStorage.getItem('condor360_showing_profile') === 'true') {
      setShowProfile(true); setShowAdminPanel(false); setShowSelector(false);
    } else if (perms.canAccessPanel) { setShowAdminPanel(true); setShowSelector(false); }
    else { setShowAdminPanel(false); setShowSelector(true); }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('condor360_current_user')
                    || sessionStorage.getItem('condor360_current_user');
    if (!storedUser) return;
    try {
      const user = JSON.parse(storedUser);
      if (!user.organization_features) user.organization_features = {};
      setCurrentUser(user); setIsAuthenticated(true);
      scheduleExpiryWarning(user.expiresIn || 900);
      applyInitialView(user);
      authService.me().then(res => {
        if (res?.data?.user) {
          const fresh = res.data.user;
          setCurrentUser(prev => {
            if (!prev) return prev;
            // Guard de boot: si los campos que importan no cambiaron, devolver
            // prev tal cual para no disparar re-renders en cascada (que terminan
            // en `new Notification` re-pidiendo el icon en loop). Comparar por
            // contenido — JSON.stringify de los campos relevantes.
            const sameRole     = prev.role === fresh.role;
            const sameFlags    = JSON.stringify(prev.organization_features || {}) === JSON.stringify(fresh.organization_features || {});
            const sameEffective = JSON.stringify(prev.effective_features || {}) === JSON.stringify(fresh.effective_features || {});
            const samePerms    = prev.can_use_checklist === fresh.can_use_checklist && prev.can_use_quick_log === fresh.can_use_quick_log;
            const sameFlow     = prev.flow_mode_enabled === fresh.flow_mode_enabled;
            if (sameRole && sameFlags && sameEffective && samePerms && sameFlow) {
              return prev;
            }
            const updated = { ...prev, ...fresh };
            try { localStorage.setItem('condor360_current_user', JSON.stringify(updated)); } catch (_) {}
            if (prev.role !== fresh.role) {
              setTimeout(() => applyInitialView(updated), 0);
            }
            return updated;
          });
        }
      }).catch((err) => {
        // Telemetría: no propagar (igual que antes), pero reportar para
        // destapar la tasa real de fallo de /me en boot (cold start de Render,
        // mobile sin red, etc.). El comportamiento de la app no cambia.
        try {
          Sentry.captureException(err, {
            tags: {
              area: 'auth_me',
              phase: 'boot',
              has_effective_features: user?.effective_features ? 'yes' : 'no',
            },
          });
        } catch (_) { /* no-op */ }
      });
    } catch (e) { console.error(e); }
  }, []);

  // ── Auto-login demo por ?demo=1 desde la landing ──────────────────
  // Un curioso que llega de la landing con ?demo=1 entra directo a la demo,
  // sin pasar por el login. Si ya hay sesión activa, se ignora el param
  // (no lo sacamos de su cuenta). El loader tapa el AuthScreen durante el
  // roundtrip a /auth/demo-login, que en cold start de Render puede tardar
  // ~50s.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('demo')) return;
    const stripDemoParam = () => {
      try {
        params.delete('demo');
        const rest = params.toString();
        const url  = window.location.pathname + (rest ? '?' + rest : '') + window.location.hash;
        window.history.replaceState(null, '', url);
      } catch (_) { /* no-op */ }
    };
    const hasSession = !!(localStorage.getItem('condor360_current_user')
                       || sessionStorage.getItem('condor360_current_user'));
    if (hasSession) {
      // El usuario real ya está logueado — respetamos su sesión y solo
      // limpiamos el param para que un refresh no vuelva a disparar demo.
      stripDemoParam();
      return;
    }
    if (shouldRedirectToDemoHost(window.location.hostname)) {
      // Red de seguridad: la demo se separó a su propia infra. Un link
      // viejo (?demo=1) que caiga en prod se manda al dominio de la demo,
      // que corre este mismo código y hace su propio auto-login con el
      // ?demo=1 preservado.
      window.location.href = DEMO_HOST_REDIRECT_TARGET;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await authService.demoLogin('admin');
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        if (cancelled) return;
        if (!data?.success || !data?.token) throw new Error('demo-login: respuesta inválida');
        storeTokens(data.token, data.refreshToken, data.user);
        const nextUser = { ...data.user, token: data.token, expiresIn: data.expiresIn };
        stripDemoParam();
        setDemoBooting(false);
        handleLogin(nextUser);
      } catch (err) {
        if (cancelled) return;
        try {
          Sentry.captureException(err, { tags: { area: 'auth_demo', phase: 'landing_autologin' } });
        } catch (_) { /* no-op */ }
        stripDemoParam();
        setDemoBootError('No pudimos preparar la demo. Probá de nuevo o iniciá sesión.');
        setDemoBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedAircraft || !isAuthenticated) return;
    saveChecklistSession({
      aircraft: selectedAircraft, sessionData, checkedItems, skippedItems,
      currentSectionIndex, currentItemIndex, pendingReminders,
      isComplete: isChecklistComplete, observationItems,
      activeInspectionId,
      // Multi-fase (Sub-etapa 3.2b) — contenedor y plan de fases, para
      // retomar a mitad de una fase ≠ prevuelo tras un refresh.
      groupId, activePhase, phasesPlan, phasesDone,
      // Sub-etapa 3.2c-ii — obligatorias/opcionales, para que el botón
      // "Saltear" siga disponible tras un refresh a mitad de chequeo.
      requiredPhases, phasesSkipped,
      // Sub-etapa 3.2c-iii — snapshots de fases cerradas, para que el PDF
      // consolidado sobreviva a un refresh a mitad de chequeo.
      phasesData,
    });
  }, [selectedAircraft, checkedItems, skippedItems, currentSectionIndex, currentItemIndex,
      isChecklistComplete, pendingReminders, observationItems]);

  useEffect(() => {
    if (selectedAircraft && currentSectionIndex < selectedAircraft.checklist.length)
      setCurrentSectionName(selectedAircraft.checklist[currentSectionIndex].section);
  }, [currentSectionIndex, selectedAircraft]);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [darkMode]);

  useEffect(() => {
    if (selectedAircraft && currentSectionIndex < selectedAircraft.checklist.length) {
      const el = document.getElementById(`item-${currentSectionIndex}-${currentItemIndex}`);
      if (el) el.scrollIntoView({ behavior:'smooth', block:'center' });
    }
  }, [currentSectionIndex, currentItemIndex, selectedAircraft]);

  useEffect(() => {
    let startY = 0, pulling = false;
    const onTouchStart = (e) => { if (window.scrollY === 0) { startY = e.touches[0].clientY; pulling = true; } };
    const onTouchEnd   = (e) => {
      if (!pulling) return;
      if (e.changedTouches[0].clientY - startY > 80) window.dispatchEvent(new CustomEvent('condor360:refresh'));
      pulling = false;
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend',   onTouchEnd,   { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend',   onTouchEnd);
    };
  }, []);

  const handleOnboardingComplete = () => { localStorage.setItem(ONBOARDING_KEY,'true'); setShowOnboarding(false); };

  const isItemSkipped = (si,ii) => skippedItems.some(s=>s.section===si&&s.item===ii);
  const isItemChecked = (si,ii) => checkedItems.some(c=>c.section===si&&c.item===ii);

  const findNextUnchecked = () => {
    if (!selectedAircraft) return { section:0, item:0 };
    for (let s=currentSectionIndex; s<selectedAircraft.checklist.length; s++) {
      const sec = selectedAircraft.checklist[s];
      for (let i=(s===currentSectionIndex?currentItemIndex+1:0); i<sec.items.length; i++) {
        if (!isItemChecked(s,i) && !isItemSkipped(s,i)) return { section:s, item:i };
      }
    }
    return { section:selectedAircraft.checklist.length, item:0 };
  };

  const handleLogin = (user) => {
    if (!user.organization_features) user.organization_features = {};
    clearChecklistSession();
    setSelectedAircraft(null); setIsChecklistComplete(false); setIsChecking(false);
    setCurrentUser(user); setIsAuthenticated(true);
    syncTokenToIDB(user.token, process.env.REACT_APP_API_URL || 'https://condor360-backend.onrender.com');
    scheduleExpiryWarning(user.expiresIn || 900);
    if (user.role === 'superadmin') { setShowAdminPanel(false); setShowSelector(false); }
    else if (usePermissions(user).canAccessPanel) { setShowAdminPanel(true); setShowSelector(false); }
    else { setShowAdminPanel(false); setShowSelector(true); }
    if (!localStorage.getItem('condor360_tour_done')) {
      tourTimerRef.current = setTimeout(() => setShowTour(true), 1200);
    }
  };

  const handleDemoSwitchView = async () => {
    if (!currentUser?.is_demo || demoSwitching) return;
    const nextRole = currentUser.role === 'admin' ? 'pilot' : 'admin';
    setDemoSwitching(true);
    try {
      const response = await authService.demoLogin(nextRole);
      const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      if (!data.success) return;

      // Cerrar overlays/modales del rol anterior antes de re-decidir la vista.
      // Sin esto, un admin que abrió "Mi Perfil" y hace switch a piloto vería
      // el perfil del admin encima del selector del piloto.
      setShowProfile(false); setShowAbout(false); setShowHistory(false);
      setShowFlightLog(false); setShowPilotTurnos(false);
      setShowWBCalculator(false); setShowNavigationPlanner(false);
      setShowPreLanding(false); setPreLandingAircraft(null);
      setShowEmergencyTrainer(false); setShowFlowMode(false);
      setOpenFlightBlock(null); setShowQuickOpen(false);
      setShowModeChoice(false); setShowPreflightModal(false);
      setPendingStartData(null); setUpsellFeature(null);
      cancelExpiryWarning();

      // Persistir access + refresh vía apiService — sin el refresh, el
      // interceptor seguiría usando el del rol anterior cuando el JWT expire.
      const nextUser = { ...data.user, token: data.token, expiresIn: data.expiresIn };
      storeTokens(data.token, data.refreshToken, data.user);
      handleLogin(nextUser);
    } catch (_) {
      // Fallar silencioso: el banner sigue visible, el visitante puede reintentar.
    } finally { setDemoSwitching(false); }
  };

  const handleLogout = () => {
    stopSpeech(); clearChecklistSession(); clearSeenTips();
    cancelExpiryWarning(); setShowSessionWarning(false);
    authService.logout();
    sessionStorage.removeItem('condor360_showing_profile');
    setCurrentUser(null); setIsAuthenticated(false);
    setShowSelector(true); setShowAdminPanel(false); setShowProfile(false);
    setSelectedAircraft(null); setIsChecklistComplete(false); setReferenceModal(null);
    setIsSpeaking(false); setObservationItems([]); setPendingObservation(null);
  };

  const handleRenewSession = async () => {
    setShowSessionWarning(false);
    try { await authService.me(); scheduleExpiryWarning(900); }
    catch { handleLogout(); }
  };

  const handleObservationRequest = (sectionIdx, itemIdx, item) => {
    setPendingObservation({ section: sectionIdx, item: itemIdx, itemObj: item });
  };

  const handleObservationConfirm = (comment) => {
    if (!pendingObservation) return;
    const { section, item, itemObj } = pendingObservation;
    const itemText = typeof itemObj === 'string' ? itemObj : itemObj?.text || '';
    const itemKey  = `${section}-${item}`;
    const photos   = itemPhotos[itemKey] || [];
    setObservationItems(prev => {
      const filtered = prev.filter(o => !(o.section === section && o.item === item));
      return [...filtered, { section, item, text: itemText, comment, photos, createdAt: new Date().toISOString() }];
    });
    setPendingObservation(null);
    safeSpeak('Observación registrada.');
  };

  const handlePreLanding = (aircraft) => {
    setPreLandingAircraft(aircraft);
    setShowPreLanding(true);
  };

  const handleStartFlow = async (aircraft) => {
    // Chequeo de bloqueo por vuelo abierto: llamamos a start ANTES de navegar.
    try {
      const res = await inspectionService.start({
        aircraft_model_id: aircraft.id,
        inspection_date:   new Date().toISOString().split('T')[0],
        inspection_time:   new Date().toTimeString().slice(0,5),
      });
      if (res?.data?.data?.id) setFlowInspectionId(res.data.data.id);
      setFlowAircraft(aircraft);
      setShowFlowMode(true);
      setShowSelector(false);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.code === 'AIRCRAFT_HAS_OPEN_FLIGHT') {
        setOpenFlightBlock(data.open_flight);
        return;
      }
      // Cualquier otro fallo: comportamiento previo (no bloqueante).
      setFlowAircraft(aircraft);
      setShowFlowMode(true);
      setShowSelector(false);
    }
  };

  const handleFlowClose = () => {
    setShowFlowMode(false);
    setFlowAircraft(null);
    setFlowInspectionId(null);
    setShowSelector(true);
  };

  const handleStartEmergency = async (aircraft) => {
    setEmergencyAircraft(aircraft);
    try {
      const { emergencyService } = await import('./services/apiService');
      const res = await emergencyService.listProcedures(aircraft.id);
      const procs = res.data?.data || [];
      if (procs.length === 0) return;
      if (procs.length === 1) {
        setEmergencyProcedureId(procs[0].id);
        setShowEmergencyTrainer(true);
        setShowSelector(false);
      } else {
        setEmergencyProcedures(procs);
        setShowEmergencySelect(true);
      }
    } catch (_) {}
  };

  const handleEmergencySelect = (procId) => {
    setEmergencyProcedureId(procId);
    setShowEmergencySelect(false);
    setShowEmergencyTrainer(true);
    setShowSelector(false);
  };

  const handleEmergencyClose = () => {
    setShowEmergencyTrainer(false);
    setEmergencyAircraft(null);
    setEmergencyProcedureId(null);
    setEmergencyProcedures([]);
    setShowEmergencySelect(false);
    setShowSelector(true);
  };

  const startChecklist = (data) => {
    // Parte 4 — Branching de modalidad según permisos del piloto.
    // canChecklist default true; canQuickLog usa solo el permiso del piloto:
    // el backend (Parte 2) ya garantiza que can_use_quick_log = true solo si
    // la org tiene la feature, así que el front no necesita re-validar
    // effective_features (no siempre hidrata en mobile). Guard 2 de
    // /api/flights/quick-open sigue siendo la red real.
    const canChecklist = currentUser?.can_use_checklist !== false;
    const canQuickLog  = currentUser?.can_use_quick_log === true;

    setPendingStartData(data);

    if (canChecklist && canQuickLog) {
      // Ambos habilitados → preguntar al piloto en el momento.
      setShowModeChoice(true);
      return;
    }
    if (canQuickLog && !canChecklist) {
      // Solo registro rápido habilitado → directo al modal de salida.
      setShowQuickOpen(true);
      return;
    }
    // Solo checklist (default actual) o ningún permiso (defensivo) → flujo histórico.
    setShowPreflightModal(true);
  };

  // Parte 4 — Handlers del registro rápido (apertura) ─────────────────
  const handleModeChooseChecklist = () => {
    setShowModeChoice(false);
    setShowPreflightModal(true);
  };

  const handleModeChooseQuickLog = () => {
    setShowModeChoice(false);
    setShowQuickOpen(true);
  };

  const handleModeChoiceDismiss = () => {
    setShowModeChoice(false);
    setPendingStartData(null);
  };

  const handleQuickOpenSuccess = () => {
    // El vuelo queda abierto, igual que en el flujo de checklist. NO hacemos
    // recheckHobbs() acá: el PostFlightModal aparece recién cuando llega el
    // push del scheduler (60' de HOBBS_NOTIFY_MINUTES) o cuando el piloto lo
    // dispara a mano desde el menú "Registrar vuelo". Refrescar inmediato
    // disparaba el post-vuelo al toque, asimétrico contra el checklist.
    setShowQuickOpen(false);
    setPendingStartData(null);
  };

  const handleQuickOpenDismiss = () => {
    setShowQuickOpen(false);
    setPendingStartData(null);
  };

  const handleQuickOpenOpenFlight = (openFlight) => {
    setShowQuickOpen(false);
    setPendingStartData(null);
    setOpenFlightBlock(openFlight);
  };

  const handlePreflightConfirm = async () => {
    const data = pendingStartData;
    if (!data) return;

    // Antes de avanzar: chequear bloqueo por vuelo abierto en la aeronave.
    let inspectionIdFromStart = null;
    let groupIdFromStart      = null;
    try {
      const res = await inspectionService.start({
        aircraft_model_id: data.aircraft.id,
        club_name:         data.clubName || null,
        inspection_date:   data.date || new Date().toISOString().split('T')[0],
        inspection_time:   new Date().toTimeString().slice(0,5),
      });
      inspectionIdFromStart = res?.data?.data?.id || null;
      groupIdFromStart      = res?.data?.data?.group_id || null;
    } catch (err) {
      const errData = err?.response?.data;
      if (err?.response?.status === 409 && errData?.code === 'AIRCRAFT_HAS_OPEN_FLIGHT') {
        // Bloqueo: NO avanzar al checklist y mostrar el aviso.
        setShowPreflightModal(false);
        setPendingStartData(null);
        setOpenFlightBlock(errData.open_flight);
        return;
      }
      // Cualquier otro error de red: seguir con el flujo (comportamiento previo).
    }

    setShowPreflightModal(false);
    setPendingStartData(null);
    if (tourTimerRef.current) { clearTimeout(tourTimerRef.current); tourTimerRef.current = null; }
    if (showTour) { localStorage.setItem('condor360_tour_done', '1'); setShowTour(false); }
    setReferenceModal(null);
    setSelectedAircraft(data.aircraft); setSessionData(prev=>({...prev,...data}));
    setShowSelector(false); setShowAdminPanel(false);
    setCurrentSectionIndex(0); setCurrentItemIndex(0);
    setCheckedItems([]); setSkippedItems([]); setPendingReminders([]); setSkippedReviewQueue([]);
    setReviewingSkipped(false); setFirstCheckDone(false); setPhraseIndex(0); setItemPhotos({});
    setIsChecklistComplete(false); setIsChecking(true); setIsSpeaking(false);
    setObservationItems([]); setPendingObservation(null);
    // Multi-fase (Sub-etapa 3.2b): plan de fases a ejecutar en ESTE chequeo —
    // siempre las que el avión tiene configuradas de verdad (phases_available,
    // Sub-etapa 3.2a), nunca un default de 3 fases. Para Madryn esto es
    // SIEMPRE ['prevuelo'] (verificado contra Neon) — el invariante que
    // garantiza que su flujo no cambia (ver CompletionScreen más abajo).
    const orgRequiredPhases = getLimit(currentUser, 'required_phases') || ['prevuelo'];
    // required_phases decide obligatoria/opcional (Sub-etapa 3.2c-ii). Acá el
    // plan a EJECUTAR son las fases que el avión tiene configuradas de
    // verdad — requiredPhases solo decide cuáles de esas son salteables.
    setPhasesPlan(data.phasesAvailable || ['prevuelo']);
    setRequiredPhases(orgRequiredPhases);
    setPhasesSkipped([]);
    setActivePhase('prevuelo');
    setPhasesDone([]);
    // Sub-etapa 3.2c-iii — arranque de chequeo nuevo, sin snapshots viejos.
    setPhasesData([]);
    clearSeenTips();
    const firstSection = data.aircraft.checklist?.[0];
    if (!firstSection) {
      alert(t('aircraft_no_checklist') || 'Esta aeronave no tiene checklist configurado. Pedile al administrador que cargue uno antes de volar.');
      setShowSelector(true); setIsChecking(false); setSelectedAircraft(null);
      return;
    }
    setCurrentSectionName(firstSection.section);
    fetchAndStoreManifest(data.aircraft.id);
    setActiveInspectionId(inspectionIdFromStart);
    setGroupId(groupIdFromStart);
    if (voiceEnabled) {
      stopSpeech(); setIsSpeaking(true);
      const greeting = getGreeting(currentUser?.name, t);
      speakText(`${greeting} ${t('lets_start_phase', { phase: t('phase_prevuelo') })}`, () => {
        const firstItem     = firstSection.items[0];
        const firstItemText = typeof firstItem === 'string' ? firstItem : firstItem.text;
        speakText(firstItemText, () => { setIsSpeaking(false); }, { aircraftId: data.aircraft.id, sectionName: firstSection.section });
      });
    }
  };

  const handleNewInspection = () => {
    stopSpeech(); clearChecklistSession(); setReferenceModal(null);
    setShowSelector(true); setIsChecklistComplete(false);
    setSelectedAircraft(null); setCheckedItems([]); setSkippedItems([]);
    setPendingReminders([]); setIsChecking(false); setCurrentSectionName('');
    setIsSpeaking(false); setObservationItems([]); setPendingObservation(null);
    // Sub-etapa 3.2c-iii — evitar que los snapshots de fases de un chequeo
    // viejo se filtren al PDF consolidado del próximo chequeo.
    setPhasesData([]);
  };

  // Multi-fase (Sub-etapa 3.2b) — cierra la fase actual y arranca la
  // siguiente colgada del MISMO group_id. A diferencia de handleNewInspection,
  // NO toca selectedAircraft (solo reemplaza su .checklist) ni groupId ni la
  // sesión guardada — resetea únicamente el progreso de la fase que arranca.
  // Solo se invoca cuando phasesPlan.length > 1 (ver CompletionScreen).
  const handleContinueToNextPhase = async (nextPhase, signatureData = null) => {
    if (!selectedAircraft || !nextPhase) return;
    try {
      const currentLang = localStorage.getItem('condor360_language') || 'es-AR';
      const langParam    = currentLang === 'en' ? 'en' : 'es';
      const checklistRes = await aircraftService.getById(selectedAircraft.id, langParam, nextPhase);
      if (!checklistRes?.data?.success || !checklistRes?.data?.data) {
        alert('No se pudo cargar el checklist de la próxima fase. Probá de nuevo.');
        return;
      }
      const nextChecklist = transformChecklistFromBackend(checklistRes.data.data.checklist);

      let inspectionIdFromStart = null;
      try {
        const startRes = await inspectionService.start({
          aircraft_model_id: selectedAircraft.id,
          phase:              nextPhase,
          group_id:           groupId,
          club_name:           sessionData.clubName || null,
          inspection_date:    new Date().toISOString().split('T')[0],
          inspection_time:    new Date().toTimeString().slice(0,5),
        });
        inspectionIdFromStart = startRes?.data?.data?.id || null;
      } catch (err) {
        const errData = err?.response?.data;
        if (err?.response?.status === 409 && errData?.code === 'AIRCRAFT_HAS_OPEN_FLIGHT') {
          setOpenFlightBlock(errData.open_flight);
          return;
        }
        alert('No se pudo iniciar la próxima fase. Probá de nuevo.');
        return;
      }

      // Sub-etapa 3.2c-iii — snapshot de la fase que se cierra, ANTES de
      // pisar selectedAircraft.checklist y resetear checkedItems/skippedItems/
      // observationItems más abajo. Es la única oportunidad de capturar este
      // estado — una vez reseteado no queda en ningún lado (ver relevamiento
      // 3.2c-iii, punto 2).
      setPhasesData(prev => [...prev, {
        phase: activePhase,
        checklist: selectedAircraft.checklist,
        checkedItems, skippedItems, observationItems,
        signatureData: signatureData || null,
        timestamp: new Date().toISOString(),
      }]);
      setSelectedAircraft(prev => ({ ...prev, checklist: nextChecklist }));
      setActiveInspectionId(inspectionIdFromStart);
      setPhasesDone(prev => [...prev, activePhase]);
      setActivePhase(nextPhase);
      setCheckedItems([]); setSkippedItems([]); setPendingReminders([]); setSkippedReviewQueue([]);
      setReviewingSkipped(false);
      setCurrentSectionIndex(0); setCurrentItemIndex(0);
      // Fotos quedan indexadas por `${section}-${item}` — si no se limpian acá,
      // una foto de la fase anterior podría "aparecer" en el mismo índice de
      // la fase nueva.
      setItemPhotos({});
      setObservationItems([]); setPendingObservation(null);
      setIsChecklistComplete(false); setIsChecking(true); setIsSpeaking(false);
      setReferenceModal(null);
      clearSeenTips();
      const firstSection = nextChecklist?.[0];
      if (firstSection) {
        setCurrentSectionName(firstSection.section);
        if (voiceEnabled) {
          stopSpeech(); setIsSpeaking(true);
          speakText(t('lets_start_phase', { phase: t(`phase_${nextPhase}`) }), () => {
            const firstItem     = firstSection.items[0];
            const firstItemText = typeof firstItem === 'string' ? firstItem : firstItem.text;
            speakText(firstItemText, () => { setIsSpeaking(false); }, { aircraftId: selectedAircraft.id, sectionName: firstSection.section });
          });
        }
      }
    } catch (err) {
      alert('No se pudo continuar con la próxima fase. Probá de nuevo.');
    }
  };

  // Sub-etapa 3.2c-ii — saltear una fase OPCIONAL (nunca prevuelo, nunca una
  // fase obligatoria — CompletionScreen solo ofrece este botón cuando
  // isNextPhaseOptional es true, y el backend lo vuelve a validar fresco de
  // DB). A diferencia de handleContinueToNextPhase, NO se carga ningún
  // checklist ni se toca activePhase — seguimos en CompletionScreen, solo
  // se registra la fase como salteada para que getPhaseProgress recalcule
  // nextPhase/isLastPhase salteándola.
  const handleSkipPhase = async (phase) => {
    if (!selectedAircraft || !phase || !groupId) return;
    try {
      await api.post('/api/inspections/skip-phase', {
        aircraft_model_id: selectedAircraft.id,
        phase,
        group_id: groupId,
      });
    } catch (err) {
      alert('No se pudo saltear la fase. Probá de nuevo.');
      return;
    }
    setPhasesSkipped(prev => [...prev, phase]);
    // Sub-etapa 3.2c-iii — la fase salteada entra al PDF consolidado como
    // banner "Fase salteada", sin detalle de ítems ni firma (nunca se
    // ejecutó).
    setPhasesData(prev => [...prev, { phase, skipped: true, timestamp: new Date().toISOString() }]);
    // Pulido de voz — si al saltear esta fase no queda ninguna otra
    // pendiente, es el mismo cierre que handleChecklistComplete dice
    // cuando isLastPhase da true (ahí no se dispara porque en ese momento
    // esta fase todavía no estaba en phasesSkipped). Si todavía queda
    // alguna fase por delante, se mantiene el silencio de hoy.
    const { isLastPhase } = getPhaseProgress(phasesPlan, activePhase, [...phasesSkipped, phase]);
    if (isLastPhase) safeSpeak(t('checklist_complete_ready'));
  };

  const navigateToItem = (targetSectionIdx, targetItemIdx) => {
    if (!selectedAircraft) return;
    const newlySkipped = [];
    if (targetSectionIdx > currentSectionIndex) {
      const sec = selectedAircraft.checklist[currentSectionIndex];
      for (let i=currentItemIndex; i<sec.items.length; i++) {
        if (!isItemChecked(currentSectionIndex,i) && !isItemSkipped(currentSectionIndex,i))
          newlySkipped.push({ section:currentSectionIndex, item:i, text:typeof sec.items[i]==='string'?sec.items[i]:sec.items[i].text });
      }
      for (let s=currentSectionIndex+1; s<targetSectionIdx; s++) {
        selectedAircraft.checklist[s].items.forEach((itm,i) => {
          if (!isItemChecked(s,i) && !isItemSkipped(s,i))
            newlySkipped.push({ section:s, item:i, text:typeof itm==='string'?itm:itm.text });
        });
      }
    } else if (targetSectionIdx===currentSectionIndex && targetItemIdx>currentItemIndex) {
      const sec = selectedAircraft.checklist[targetSectionIdx];
      for (let i=currentItemIndex; i<targetItemIdx; i++) {
        if (!isItemChecked(targetSectionIdx,i) && !isItemSkipped(targetSectionIdx,i))
          newlySkipped.push({ section:targetSectionIdx, item:i, text:typeof sec.items[i]==='string'?sec.items[i]:sec.items[i].text });
      }
    }
    if (newlySkipped.length>0) {
      setSkippedItems(prev=>[...prev,...newlySkipped]); setPendingReminders(prev=>[...prev,...newlySkipped]);
      if (pendingReminders.length+newlySkipped.length===1) safeSpeak(`No olvides ${newlySkipped[0].text.toLowerCase()}`);
      else safeSpeak('Recorda que omitiste chequear varios items');
    }
    setCurrentSectionIndex(targetSectionIdx); setCurrentItemIndex(targetItemIdx);
    setIsChecking(true); setJustConfirmed(false); setPendingItem(null);
  };

  const startReviewSkipped = () => {
    const map = new Map();
    [...skippedItems,...pendingReminders].forEach(s=>map.set(`${s.section}-${s.item}`,s));
    const unique = Array.from(map.values()).sort((a,b)=>(a.section-b.section)||(a.item-b.item));
    if (unique.length===0) return;
    setSkippedReviewQueue(unique); setReviewIndex(0);
    setIsChecklistComplete(false); setShowReviewModal(true); setVoiceEnabled(false);
  };

  const advanceToItem = (sectionIdx, itemIdx) => {
    setCurrentSectionIndex(sectionIdx); setCurrentItemIndex(itemIdx);
    setIsChecking(true); setJustConfirmed(false);
    const sec      = selectedAircraft.checklist[sectionIdx];
    const nextItem = sec.items[itemIdx];
    const text     = typeof nextItem === 'string' ? nextItem : nextItem.text;
    safeSpeak(`${phrases[phraseIndex % phrases.length]} ${text.toLowerCase()}.`, () => {}, { sectionName: sec.section, itemText: text });
    setPhraseIndex(p => p + 1);
  };

  const handleItemCheck = (sectionIdx, itemIdx, item) => {
    if (!selectedAircraft) return;
    if (isItemChecked(sectionIdx,itemIdx)) { safeSpeak(i18n.language === 'en' ? 'That item is already checked.' : 'Ese ítem ya está chequeado.'); return; }
    if (isItemSkipped(sectionIdx,itemIdx)) {
      setSkippedItems(prev=>prev.filter(s=>!(s.section===sectionIdx&&s.item===itemIdx)));
      setPendingReminders(prev=>prev.filter(p=>!(p.section===sectionIdx&&p.item===itemIdx)));
    }
    setCheckedItems(prev=>[...prev,{ section:sectionIdx, item:itemIdx, status:'checked' }]);
    setIsChecking(false); setJustConfirmed(true); setPendingItem(null);
    stopSpeech(); setIsSpeaking(false); setFirstCheckDone(true); setReferenceModal(null);
    const itemText    = (typeof item==='string' ? item : item.text).toLowerCase();
    const sectionName = selectedAircraft.checklist[sectionIdx].section;
    const afterSpeak  = () => {
      if (reviewingSkipped) {
        setSkippedReviewQueue(prevQueue => {
          const remaining = prevQueue.filter(q=>!(q.section===sectionIdx&&q.item===itemIdx));
          if (remaining.length>0) {
            const next=remaining[0];
            setTimeout(() => {
              setCurrentSectionIndex(next.section); setCurrentItemIndex(next.item);
              setIsChecking(true); setJustConfirmed(false);
              if (voiceEnabled) {
                setIsSpeaking(true);
                speakText(`${next.text.toLowerCase()}.`, () => { setIsSpeaking(false); }, { aircraftId:selectedAircraft?.id, sectionName:selectedAircraft.checklist[next.section].section });
              }
            }, 300);
          } else {
            setReviewingSkipped(false);
            setTimeout(() => {
              const np=findNextUnchecked();
              if (np.section>=selectedAircraft.checklist.length) handleChecklistComplete();
              else advanceToItem(np.section, np.item);
            }, 300);
          }
          return remaining;
        });
        return;
      }
      const nextPos = findNextUnchecked();
      if (nextPos.section>=selectedAircraft.checklist.length) handleChecklistComplete();
      else setTimeout(() => advanceToItem(nextPos.section, nextPos.item), 200);
    };
    playChime(afterSpeak);
  };

  const [recordedHobbs, setRecordedHobbs] = useState(null);
  const handleHobbsRecord = async (value) => {
    if (!selectedAircraft) return;
    setRecordedHobbs(value);
    try {
      // Fix bug hobbs huérfano (post-3.2b): sin inspection_id acá, el
      // hobbs_record nace sin vincular a la inspección en curso y el guard
      // relajado de 3.2b (join hobbs_records.inspection_id →
      // inspections.group_id) nunca puede hacer match al pasar de fase.
      await api.post(`/api/aircraft/${selectedAircraft.id}/hobbs`, { hobbs_start: value, inspection_id: activeInspectionId });
    }
    catch (e) { console.error('Hobbs record error:', e); }
  };

  const handleOilRecord = async (value) => {
    if (!selectedAircraft) return;
    try {
      await api.post(`/api/aircraft/${selectedAircraft.id}/oil`, { oil_start_qts: value, inspection_id: activeInspectionId });
    }
    catch (e) { console.error('Oil record error:', e); }
  };

  const [recordedFuel, setRecordedFuel] = useState({ left: null, right: null, unit: null });
  const handleFuelRecord = async (payload) => {
    if (!selectedAircraft) return;
    const left  = payload?.left  != null && payload.left  !== '' ? parseFloat(payload.left)  : null;
    const right = payload?.right != null && payload.right !== '' ? parseFloat(payload.right) : null;
    if (left == null && right == null) return;
    setRecordedFuel(prev => ({
      left:  left  != null ? left  : prev.left,
      right: right != null ? right : prev.right,
      unit:  selectedAircraft.fuel_unit || prev.unit || null,
    }));
    try {
      const body = { inspection_id: activeInspectionId };
      if (left  != null) body.fuel_start_left  = left;
      if (right != null) body.fuel_start_right = right;
      await api.post(`/api/aircraft/${selectedAircraft.id}/fuel`, body);
    }
    catch (e) { console.error('Fuel record error:', e); }
  };

  const handleItemInteraction = (sectionIdx, itemIdx, item) => {
    if (isItemChecked(sectionIdx,itemIdx)) return;
    if (reviewingSkipped) {
      if (sectionIdx===currentSectionIndex&&itemIdx===currentItemIndex) handleItemCheck(sectionIdx,itemIdx,item);
      else safeSpeak('Estamos revisando los ítems pendientes. Chequeá el ítem actual.');
      return;
    }
    const isFuture      = sectionIdx>currentSectionIndex||(sectionIdx===currentSectionIndex&&itemIdx>currentItemIndex);
    const isPastSkipped = isItemSkipped(sectionIdx,itemIdx);
    if (isFuture) navigateToItem(sectionIdx,itemIdx);
    else if (isPastSkipped) handleGoBackToItem({ section:sectionIdx, item:itemIdx, text:typeof item==='string'?item:item.text });
    else handleItemCheck(sectionIdx,itemIdx,item);
  };

  const handleVoiceConfirm = () => {
    if (selectedAircraft&&isChecking&&!justConfirmed) {
      const sec=selectedAircraft.checklist[currentSectionIndex];
      if (sec&&currentItemIndex<sec.items.length) handleItemCheck(currentSectionIndex,currentItemIndex,sec.items[currentItemIndex]);
    }
  };

  const handleGoBackToItem = (skippedItem) => {
    if (!skippedItem) return;
    setCurrentSectionIndex(skippedItem.section); setCurrentItemIndex(skippedItem.item);
    setIsChecking(true); setJustConfirmed(false);
    safeSpeak(i18n.language === 'en' ? `Let's go back to ${skippedItem.text.toLowerCase()}.` : `Volvamos a ${skippedItem.text.toLowerCase()}.`, () => {}, { sectionName:selectedAircraft.checklist[skippedItem.section].section });
  };

  const handleDismissReminder = (item=null) => {
    if (item) { setSkippedItems(p=>p.filter(s=>s!==item)); setPendingReminders(p=>p.filter(r=>r!==item)); }
  };

  const handleChecklistComplete = () => {
    setIsChecklistComplete(true);
    // Sub-etapa 3.2c-i: remate de voz condicionado por fase — mismo cálculo
    // isLastPhase/nextPhase que usa el render para el botón "Continuar con..."
    // (App.js, cerca de getPhaseProgress más abajo), extraído a
    // utils/checklist.js para no duplicarlo. phasesSkipped (3.2c-ii) para
    // que el remate no ofrezca una fase que el piloto ya salteó.
    const { isLastPhase, nextPhase } = getPhaseProgress(phasesPlan, activePhase, phasesSkipped);
    const closing = isLastPhase
      ? t('checklist_complete_ready')
      : t('checklist_complete_next_phase', { phase: t(`phase_${nextPhase}`) });

    if (skippedItems.length > 0 || pendingReminders.length > 0) {
      safeSpeak(t('checklist_complete_pending'));
    } else if (observationItems.length > 0) {
      const obsKey = observationItems.length === 1
        ? 'checklist_complete_observation_one'
        : 'checklist_complete_observation_other';
      safeSpeak(`${t(obsKey, { count: observationItems.length })} ${closing}`);
    } else {
      safeSpeak(closing);
    }
  };

  const handleReviewItemInModal = (index) => {
    const item=skippedReviewQueue[index];
    if (!item) return;
    setCheckedItems(prev=>[...prev,{section:item.section,item:item.item,status:'checked'}]);
    setSkippedItems(prev=>prev.filter(s=>!(s.section===item.section&&s.item===item.item)));
    setPendingReminders(prev=>prev.filter(p=>!(p.section===item.section&&p.item===item.item)));
    if (index+1<skippedReviewQueue.length) { setReviewIndex(index+1); }
    else {
      setShowReviewModal(false); setReviewIndex(0); setVoiceEnabled(true);
      setTimeout(() => {
        if (findNextUnchecked().section>=selectedAircraft.checklist.length) {
          speakText(t('all_pending_reviewed'),()=>{}); setIsChecklistComplete(true);
        } else { speakText(t('excellent_pending_reviewed'),()=>{}); }
      }, 150);
    }
  };

  const handleToggleVoice    = () => setVoiceEnabled(p=>!p);
  const handleToggleDarkMode = () => setDarkMode(p=>!p);
  const handleUpdateSession  = (d) => setSessionData(d);
  const handleAddPhoto       = (key,data,name) => setItemPhotos(prev=>({...prev,[key]:[...(prev[key]||[]),{data,name}]}));
  const handleRemovePhoto    = (key,idx) => setItemPhotos(prev=>({...prev,[key]:prev[key].filter((_,i)=>i!==idx)}));
  const handleSaveCustomChecklist = (updated) => {
    setCustomAircrafts(prev=>{ const i=prev.findIndex(a=>a.id===updated.id); if(i>=0){const a=[...prev];a[i]=updated;return a;} return [...prev,updated]; });
    setSelectedAircraft(updated); setShowEditor(false);
    safeSpeak('Checklist actualizada correctamente.');
  };

  // ── Renders ───────────────────────────────────────────────────────
  if (isAppLoading) return <AnimatePresence mode="wait"><AnimatedSplash key="splash" onComplete={handleSplashComplete} /></AnimatePresence>;
  if (demoBooting) return (
    <div style={{
      position:'fixed', inset:0, display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', gap:24,
      background:'#03050d', color:'#e8edf7', fontFamily:'Cabinet Grotesk,sans-serif',
      textAlign:'center', padding:'0 24px', zIndex:9999,
    }}>
      <div style={{
        width:56, height:56, borderRadius:'50%',
        border:'3px solid rgba(59,126,255,0.2)',
        borderTopColor:'#3b7eff',
        animation:'condor-demo-spin 1s linear infinite',
      }} />
      <style>{`@keyframes condor-demo-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ fontSize:20, fontWeight:700 }}>Preparando la demo…</div>
      <div style={{ fontSize:14, color:'rgba(232,237,247,0.6)', maxWidth:360, lineHeight:1.5 }}>
        Nuestro backend puede tardar hasta un minuto en despertar. Ya casi.
      </div>
    </div>
  );
  if (!isAuthenticated) return <AuthScreen onLogin={handleLogin} darkMode={darkMode} demoBootError={demoBootError} />;

  // ── Super Admin Panel — impersonation ───────────────────────────
  if (currentUser?.role === 'superadmin' && impersonatedOrg) return (
    <>
      <MaintenanceBanner orgId={impersonatedOrg.id} />
      <AdminPanel
        user={currentUser}
        userRole="admin"
        darkMode={darkMode}
        onToggleDark={handleToggleDarkMode}
        onLogout={handleLogout}
        onGoHome={() => setImpersonatedOrg(null)}
        impersonatedOrgId={impersonatedOrg.id}
        impersonatedOrgName={impersonatedOrg.name}
      />
      <ImpersonationBar orgName={impersonatedOrg.name} onLogout={() => setImpersonatedOrg(null)} t={t} />
      <HelpButton darkMode={darkMode} />
    </>
  );

  // ── Super Admin Panel ────────────────────────────────────────────
  if (currentUser?.role === 'superadmin') return (
    <>
      <SuperAdminPanel
        user={currentUser}
        onLogout={handleLogout}
        darkMode={darkMode}
        onToggleDark={handleToggleDarkMode}
        onImpersonate={(org) => setImpersonatedOrg(org)}
      />
      <HelpButton darkMode={darkMode} />
    </>
  );

  // ── Acerca de ─────────────────────────────────────────────────────
  // Va ANTES del guard de Admin Panel para que About pueda renderizarse
  // "encima" del panel: showAdminPanel queda intacto y reaparece al cerrar.
  if (showAbout) {
    return (
      <>
        <AboutPage
          darkMode={darkMode}
          currentUser={currentUser}
          onBack={() => setShowAbout(false)}
        />
        {['admin', 'instructor', 'superadmin'].includes(currentUser?.role) && (
          <HelpButton darkMode={darkMode} />
        )}
      </>
    );
  }

  // ── Admin Panel ───────────────────────────────────────────────────
  if (showAdminPanel) return (
    <>
      <MaintenanceBanner orgId={currentUser?.organization_id} />
      <AdminPanel
        user={currentUser}
        userRole={currentUser?.role}
        darkMode={darkMode}
        onToggleDark={handleToggleDarkMode}
        onLogout={handleLogout}
        onGoHome={() => { setShowAdminPanel(false); setShowSelector(true); }}
        orgFeatures={currentUser?.organization_features || {}}
        isAuthenticated={isAuthenticated}
        onShowProfile={() => { setShowAdminPanel(false); setShowProfile(true); }}
        onAbout={() => setShowAbout(true)}
      />
      {['admin', 'instructor'].includes(currentUser?.role) && (
        <HelpButton darkMode={darkMode} />
      )}
      {currentUser?.is_demo && (
        <DemoBanner currentUser={currentUser} onSwitchView={handleDemoSwitchView} switching={demoSwitching} isMobile={isMobile} />
      )}
      <AnimatePresence>
        {showSessionWarning && <SessionWarningModal darkMode={darkMode} onRenew={handleRenewSession} onLogout={handleLogout} />}
        {demoBlockedMessage !== null && <DemoBlockedModal darkMode={darkMode} message={demoBlockedMessage} onClose={() => setDemoBlockedMessage(null)} />}
      </AnimatePresence>
    </>
  );

  // ── Mi Perfil ─────────────────────────────────────────────────────
  if (showProfile) {
    return (
      <>
        <MyProfile
          currentUser={currentUser}
          darkMode={darkMode}
          onBack={() => {
            setShowProfile(false);
            if (permissions.canAccessPanel) setShowAdminPanel(true);
          }}
          onUserUpdated={(updated) => {
            const merged = { ...currentUser, ...updated };
            setCurrentUser(merged);
            try { localStorage.setItem('condor360_current_user', JSON.stringify(merged)); } catch (_) {}
            setShowProfile(false);
          }}
        />
        {['admin', 'instructor', 'superadmin'].includes(currentUser?.role) && (
          <HelpButton darkMode={darkMode} />
        )}
      </>
    );
  }

  // ── Emergency Trainer ──────────────────────────────────────────────
  if (showEmergencyTrainer && emergencyAircraft && emergencyProcedureId && hasFeature(currentUser, 'emergency_trainer')) {
    return (
      <EmergencyTrainer
        aircraft={emergencyAircraft}
        procedureId={emergencyProcedureId}
        currentUser={currentUser}
        darkMode={darkMode}
        orgFeatures={currentUser?.organization_features || {}}
        onClose={handleEmergencyClose}
      />
    );
  }

  // ── Flow Mode ─────────────────────────────────────────────────────
  if (showFlowMode && flowAircraft && hasFeature(currentUser, 'flow_mode')) {
    return (
      <FlowScreen
        aircraft={flowAircraft}
        inspectionId={flowInspectionId}
        currentUser={currentUser}
        darkMode={darkMode}
        orgFeatures={currentUser?.organization_features || {}}
        onClose={handleFlowClose}
      />
    );
  }

  // ── Selector ──────────────────────────────────────────────────────
  if (showSelector) {
    const demoSelectorTopOffset = (isMobile && currentUser?.is_demo) ? DEMO_SELECTOR_MOBILE_TOP_OFFSET : 0;
    return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4"
      style={{
        position:'relative',
        ...(demoSelectorTopOffset ? {
          alignItems:'flex-start',
          paddingTop: demoSelectorTopOffset,
          boxSizing:'border-box',
        } : {}),
      }}>
      <DesktopBackground variant={darkMode?'dark':'default'} />
      <MaintenanceBanner orgId={currentUser?.organization_id} />
      <div style={{ position:'relative', zIndex:1, width:'100%', display:'flex', justifyContent:'center' }}>

        {permissions.canAccessPanel && (
          <motion.button onClick={()=>setShowAdminPanel(true)} initial={{ opacity:0,x:-20 }} animate={{ opacity:1,x:0 }} whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }}
            style={{ position:'fixed',top: (isMobile && currentUser?.is_demo) ? `${DEMO_MOBILE_TOP_OFFSET}px` : '16px',left:'16px',zIndex:50,display:'flex',alignItems:'center',gap:'8px',padding:'10px 16px',borderRadius:'16px',background:'rgba(255,255,255,0.9)',backdropFilter:'blur(12px)',border:'1px solid rgba(59,130,246,0.15)',boxShadow:'0 4px 20px rgba(59,130,246,0.1)',cursor:'pointer',fontFamily:'inherit',fontSize:'14px',fontWeight:700,color:'rgba(30,58,138,0.8)' }}>
            <span style={{ fontSize:'16px' }}>←</span> {t('panel_admin')}
          </motion.button>
        )}

        <div style={{ position:'fixed', top: (isMobile && currentUser?.is_demo) ? `${DEMO_MOBILE_TOP_OFFSET}px` : '16px', right:'16px', zIndex:50, display:'flex', alignItems:'center', gap:'8px' }}>
          <NotificationManager darkMode={darkMode} isAuthenticated={isAuthenticated} />
          <SelectorMenu
            permissions={permissions}
            darkMode={darkMode}
            onAdmin={()=>setShowAdminPanel(true)}
            onOnboarding={()=>setShowOnboarding(true)}
            onFlightLog={()=>setShowFlightLog(true)}
            onPilotTurnos={()=>setShowPilotTurnos(true)}
            onWBCalculator={()=>setShowWBCalculator(true)}
            onNavigationPlanner={()=>setShowNavigationPlanner(true)}
            onProfile={() => setShowProfile(true)}
            onAbout={() => setShowAbout(true)}
            onLogout={handleLogout}
            onRegisterFlightManual={currentUser?.role === 'pilot' ? async () => {
              if (pendingHobbs) return; // modal ya visible
              const pending = await recheckHobbs();
              if (!pending || pending.length === 0) {
                alert(t('no_pending_flights') || 'No hay vuelos pendientes para registrar');
              }
            } : null}
            orgFeatures={currentUser?.organization_features || {}}
            onUpsell={(feat) => setUpsellFeature(feat)}
            currentUser={currentUser}
          />
        </div>

        <AircraftSelector
          onSelectAircraft={startChecklist}
          onClubSubmit={startChecklist}
          userRole={currentUser?.role}
          preselectedAircraftId={qrAircraftId}
          onQrHandled={() => setQrAircraftId(null)}
          onPreLanding={handlePreLanding}
          orgFeatures={currentUser?.organization_features || {}}
          onUpsell={(feat) => setUpsellFeature(feat)}
          onStartFlow={handleStartFlow}
          onStartEmergency={handleStartEmergency}
          currentUser={currentUser}
        />
      </div>

      <AnimatePresence>
        {showOnboarding && <OnboardingSlides key="onboarding" onComplete={handleOnboardingComplete} darkMode={darkMode} />}
      </AnimatePresence>
      <AnimatePresence>
        {showFlightLog && <PilotFlightLog darkMode={darkMode} currentUser={currentUser} onClose={()=>setShowFlightLog(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showPilotTurnos && <PilotTurnos darkMode={darkMode} currentUser={currentUser} onClose={()=>setShowPilotTurnos(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showWBCalculator && hasFeature(currentUser, 'wb') && <WBCalculator darkMode={darkMode} currentUser={currentUser} onClose={()=>setShowWBCalculator(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showNavigationPlanner && hasFeature(currentUser, 'navigation') && <NavigationPlanner darkMode={darkMode} currentUser={currentUser} onClose={()=>setShowNavigationPlanner(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {showPreLanding && preLandingAircraft && hasFeature(currentUser, 'pre_landing') && (
          <PreLandingChecklist
            aircraft={preLandingAircraft}
            darkMode={darkMode}
            config={currentUser?.organization_features?.pre_landing_config || {}}
            onClose={() => { setShowPreLanding(false); setPreLandingAircraft(null); }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showSessionWarning && <SessionWarningModal darkMode={darkMode} onRenew={handleRenewSession} onLogout={handleLogout} />}
        {demoBlockedMessage !== null && <DemoBlockedModal darkMode={darkMode} message={demoBlockedMessage} onClose={() => setDemoBlockedMessage(null)} />}
      </AnimatePresence>
      <AnimatePresence>
        {upsellFeature && (
          <UpsellModal
            feature={upsellFeature}
            currentPlan={currentUser?.organization_plan || 'basic'}
            darkMode={darkMode}
            onClose={() => setUpsellFeature(null)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showPreflightModal && pendingStartData && (
          <PreflightReadyModal
            darkMode={darkMode}
            aircraft={pendingStartData.aircraft}
            pilotName={currentUser?.name}
            onConfirm={handlePreflightConfirm}
            tFn={t}
          />
        )}
      </AnimatePresence>

      {/* Parte 4 — Registro rápido: elección de modalidad / modal de salida */}
      <AnimatePresence>
        {showModeChoice && pendingStartData && (
          <ModeChoiceModal
            aircraft={pendingStartData.aircraft}
            darkMode={darkMode}
            onChecklist={handleModeChooseChecklist}
            onQuickLog={handleModeChooseQuickLog}
            onDismiss={handleModeChoiceDismiss}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showQuickOpen && pendingStartData && (
          <QuickOpenModal
            aircraft={pendingStartData.aircraft}
            darkMode={darkMode}
            onOpened={handleQuickOpenSuccess}
            onDismiss={handleQuickOpenDismiss}
            onOpenFlightConflict={handleQuickOpenOpenFlight}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {openFlightBlock && (
          <motion.div
            key="open-flight-block"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position:'fixed', inset:0, zIndex:220, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(8px)' }}
          >
            <motion.div initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:20 }}
              style={{ width:'100%', maxWidth:420, borderRadius:24, background: darkMode?'#0f172a':'white', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}
            >
              <div style={{ height:4, background:'linear-gradient(90deg,#f59e0b,#ef4444)' }} />
              <div style={{ padding:'24px 24px 20px' }}>
                <div style={{ fontSize:22, fontWeight:700, color: darkMode?'#f1f5f9':'#0f172a', marginBottom:8 }}>
                  Vuelo sin cerrar
                </div>
                <div style={{ fontSize:14, lineHeight:1.5, color: darkMode?'#cbd5e1':'#475569', marginBottom:16 }}>
                  No se puede iniciar un chequeo nuevo en <b>{openFlightBlock.aircraft_label || 'esta aeronave'}</b> porque ya hay un vuelo abierto que falta cerrar.
                </div>
                <div style={{
                  background: darkMode?'#1e293b':'#f8fafc',
                  borderRadius:12, padding:'12px 14px', fontSize:13, lineHeight:1.6,
                  color: darkMode?'#e2e8f0':'#334155', marginBottom:16,
                }}>
                  <div><b>Piloto:</b> {openFlightBlock.recorded_by_name || '—'}</div>
                  {openFlightBlock.hobbs_start != null && (
                    <div><b>Horómetro inicial:</b> {openFlightBlock.hobbs_start} hs</div>
                  )}
                  {openFlightBlock.created_at && (
                    <div><b>Fecha:</b> {new Date(openFlightBlock.created_at).toLocaleString('es-AR')}</div>
                  )}
                </div>
                <div style={{ fontSize:13, color: darkMode?'#94a3b8':'#64748b', marginBottom:18 }}>
                  {(['admin','instructor','superadmin'].includes(currentUser?.role))
                    ? 'Cerralo cargando el horómetro final o anulalo desde el panel de vuelos pendientes.'
                    : 'Pedile al piloto que cargue el horómetro final o avisale a un admin/instructor para que lo cierre.'}
                </div>
                <button
                  onClick={() => setOpenFlightBlock(null)}
                  style={{
                    width:'100%', padding:'12px 16px', borderRadius:12, border:'none',
                    background:'linear-gradient(90deg,#3b82f6,#2563eb)', color:'white',
                    fontSize:15, fontWeight:600, cursor:'pointer',
                  }}
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ── Emergency procedure selector modal ── */}
      <AnimatePresence>
        {showEmergencySelect && emergencyProcedures.length > 0 && (
          <motion.div key="emergency-select" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(8px)' }}>
            <motion.div initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:20 }}
              style={{ width:'100%', maxWidth:380, borderRadius:24, background: darkMode?'#0f172a':'white', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
              <div style={{ height:4, background:'linear-gradient(90deg,#ef4444,#f97316)' }} />
              <div style={{ padding:'24px 20px' }}>
                <div style={{ fontSize:40, textAlign:'center', marginBottom:12 }}>🚨</div>
                <h3 style={{ margin:'0 0 16px', textAlign:'center', fontSize:18, fontWeight:800, color: darkMode?'white':'#0f172a' }}>
                  {t('choose_procedure')}
                </h3>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {emergencyProcedures.map(proc => (
                    <motion.button key={proc.id} whileTap={{ scale:0.97 }}
                      onClick={() => handleEmergencySelect(proc.id)}
                      style={{ width:'100%', padding:'14px 16px', borderRadius:14, border:`1px solid ${darkMode?'rgba(255,255,255,0.1)':'#e2e8f0'}`, background: darkMode?'rgba(255,255,255,0.04)':'#f8fafc', cursor:'pointer', fontFamily:'inherit', textAlign:'left', display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:20 }}>{({engine_failure:'\u{1F534}',fire:'\u{1F525}',electrical:'\u26A1',ditching:'\u{1F30A}',gear:'\u2699\uFE0F'})[proc.emergency_type] || '\u26A0\uFE0F'}</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color: darkMode?'white':'#0f172a' }}>{proc.title}</div>
                        <div style={{ fontSize:12, color: darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>{proc.items?.length || 0} {t('steps')} · {proc.benchmark_seconds}s</div>
                      </div>
                    </motion.button>
                  ))}
                </div>
                <button onClick={() => { setShowEmergencySelect(false); setEmergencyProcedures([]); }}
                  style={{ marginTop:16, width:'100%', padding:'12px', borderRadius:14, border:`1px solid ${darkMode?'rgba(255,255,255,0.1)':'#e2e8f0'}`, background:'transparent', color: darkMode?'rgba(255,255,255,0.5)':'#64748b', fontWeight:600, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>
                  {t('cancel')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {pendingHobbs && (
          <PostFlightModal
            record={pendingHobbs}
            darkMode={darkMode}
            onComplete={completeHobbs}
            onDismiss={dismissHobbs}
          />
        )}
      </AnimatePresence>
      {paymentResult && (
        <PaymentResult
          status={paymentResult.status}
          plan={paymentResult.plan}
          onClose={() => setPaymentResult(null)}
        />
      )}
      {['admin', 'instructor'].includes(currentUser?.role) && (
        <HelpButton darkMode={darkMode} />
      )}
      {currentUser?.is_demo && (
        <DemoBanner currentUser={currentUser} onSwitchView={handleDemoSwitchView} switching={demoSwitching} isMobile={isMobile} />
      )}
    </div>
    );
  }

  // ── Checklist complete ────────────────────────────────────────────
  const currentChecklist  = selectedAircraft?.checklist || [];
  const currentSection    = currentChecklist[currentSectionIndex];
  const currentItem       = currentSection?.items[currentItemIndex];
  const currentItemText   = typeof currentItem==='string' ? currentItem : currentItem?.text;
  const isSectionComplete = (idx) => {
    const sec=currentChecklist[idx];
    return sec.items.every((_,i)=>checkedItems.some(c=>c.section===idx&&c.item===i)||skippedItems.some(s=>s.section===idx&&s.item===i));
  };

  if (isChecklistComplete) {
    const allPending    = [...skippedItems,...pendingReminders];
    const uniquePending = Array.from(new Map(allPending.map(i=>[`${i.section}-${i.item}`,i])).values());
    // Multi-fase (Sub-etapa 3.2b): isLastPhase compara contra la ÚLTIMA fase
    // del plan REAL del avión (phasesPlan, armado en handlePreflightConfirm a
    // partir de phases_available — nunca un default de 3). Con
    // phasesPlan=['prevuelo'] (caso Madryn), isLastPhase da true en el único
    // render posible → CompletionScreen se comporta idéntico a hoy.
    // (Sub-etapa 3.2c-i: cálculo extraído a getPhaseProgress — mismo usado
    // por la voz de cierre en handleChecklistComplete. Sub-etapa 3.2c-ii:
    // se le suma phasesSkipped para saltar las opcionales ya salteadas, y
    // se deriva isNextPhaseOptional — nextPhase es opcional cuando el avión
    // la tiene configurada pero la org no la exige (requiredPhases).)
    const { isLastPhase, nextPhase } = getPhaseProgress(phasesPlan, activePhase, phasesSkipped);
    const isNextPhaseOptional = !!nextPhase && !requiredPhases.includes(nextPhase);
    return (
      <>
        <CompletionScreen
          hasPendingItems={uniquePending.length>0} uniquePending={uniquePending}
          onReviewSkipped={startReviewSkipped} onNewInspection={handleNewInspection}
          sessionData={{...sessionData, hobbsMeter: recordedHobbs, fuelStart: recordedFuel}} checkedItems={checkedItems} skippedItems={skippedItems}
          darkMode={darkMode} weatherData={weatherData}
          observationItems={observationItems} pilotName={currentUser?.name}
          orgFeatures={currentUser?.organization_features || {}}
          activeInspectionId={activeInspectionId}
          isLastPhase={isLastPhase} activePhase={activePhase} nextPhase={nextPhase}
          onContinuePhase={handleContinueToNextPhase}
          isNextPhaseOptional={isNextPhaseOptional} onSkipPhase={handleSkipPhase}
          // Sub-etapa 3.2c-iii — snapshots de fases ya cerradas y el
          // checklist REAL de la fase activa (selectedAircraft.checklist,
          // no sessionData.aircraft.checklist — ese queda congelado en el
          // checklist de la fase 1, ver relevamiento 3.2c-iii). Con
          // phasesData=[] (Madryn/1ra fase) CompletionScreen no cambia nada.
          phasesData={phasesData} activePhaseChecklist={selectedAircraft.checklist}
        />
        <AnimatePresence>
          {showSessionWarning && <SessionWarningModal darkMode={darkMode} onRenew={handleRenewSession} onLogout={handleLogout} />}
        {demoBlockedMessage !== null && <DemoBlockedModal darkMode={darkMode} message={demoBlockedMessage} onClose={() => setDemoBlockedMessage(null)} />}
        </AnimatePresence>
      </>
    );
  }

  // ── Checklist view ────────────────────────────────────────────────
  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 p-4 ${darkMode?'dark':''}`} style={{ position:'relative' }}>
      <DesktopBackground variant={darkMode?'dark':'default'} />
      <MaintenanceBanner orgId={currentUser?.organization_id} />
      <style>{`
        @keyframes condor-shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }
        @keyframes condor-float   { 0%,100%{transform:translateX(0) rotate(0deg)} 50%{transform:translateX(3px) rotate(3deg)} }
      `}</style>

      <div className="container mx-auto max-w-2xl" style={{ position:'relative', zIndex:1, paddingBottom:'100px' }}>
        <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ duration:0.6 }}>
          {/* HEADER */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'24px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <motion.div style={{ fontSize:'38px', lineHeight:1, animation:'condor-float 4s ease-in-out infinite' }}>
                ✈️
              </motion.div>
              <div>
                <h1 style={{ margin:0,fontSize:'clamp(1.5rem,6vw,2.4rem)',fontWeight:900,letterSpacing:'-1px',lineHeight:1,backgroundImage:'linear-gradient(110deg,#1d4ed8 0%,#6d28d9 40%,#2563eb 60%,#7c3aed 100%)',backgroundSize:'200% auto',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text',animation:'condor-shimmer 5s linear infinite' }}>Condor360</h1>
                <p style={{ margin:0,fontSize:'9px',fontWeight:700,color:darkMode?'rgba(200,210,230,0.5)':'rgba(60,80,120,0.5)',textTransform:'uppercase',letterSpacing:'0.18em' }}>{t('aeronautical_management')}</p>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <NotificationManager darkMode={darkMode} isAuthenticated={isAuthenticated} />
              <HeaderMenu permissions={permissions} darkMode={darkMode}
                onAdmin={()=>setShowAdminPanel(true)}
                onBack={()=>{ stopSpeech(); setIsSpeaking(false); setReferenceModal(null); setShowSelector(true); setSelectedAircraft(null); setCheckedItems([]); setSkippedItems([]); setPendingReminders([]); setIsChecking(false); setIsChecklistComplete(false); setCurrentSectionName(''); }}
                onHistory={()=>setShowHistory(true)}
                onEdit={()=>setShowEditor(true)}
                onHelp={()=>setShowOnboarding(true)}
                onAbout={() => setShowAbout(true)}
                onLogout={handleLogout}
              />
            </div>
          </div>

          {pendingReminders.length===1 && (
            <SkipReminder message={`Cuidado! no olvides ${pendingReminders[0].text}`} onDismiss={()=>handleDismissReminder(pendingReminders[0])} skippedItem={pendingReminders[0]} onGoBackToItem={handleGoBackToItem} autoDismiss={false} darkMode={darkMode} />
          )}

          {pendingReminders.length>1 && !reviewingSkipped && (
            <div style={{ position:'fixed',top:'20px',left:'50%',transform:'translateX(-50%)',zIndex:50,width:'min(340px,calc(100vw - 32px))' }}>
              <motion.div
                initial={{ opacity:0,y:-20,scale:0.9 }} animate={{ opacity:1,y:0,scale:1 }} exit={{ opacity:0,y:-20,scale:0.9 }}
                transition={{ type:'spring',stiffness:150,damping:20 }}
                style={{ borderRadius:'22px',overflow:'hidden',background:darkMode?'rgba(18,8,2,0.93)':'rgba(255,255,255,0.93)',backdropFilter:'blur(20px)',boxShadow:'0 16px 52px rgba(239,68,68,0.25)',border:darkMode?'1px solid rgba(245,158,11,0.18)':'1px solid rgba(245,158,11,0.28)' }}>
                <motion.div animate={{ backgroundPosition:['0% 50%','100% 50%','0% 50%'] }} transition={{ duration:2.5,repeat:Infinity,ease:'linear' }} style={{ height:'3px',background:'linear-gradient(90deg,#f59e0b,#ef4444,#f97316,#ef4444,#f59e0b)',backgroundSize:'300% 100%' }} />
                <div style={{ padding:'14px 16px 10px',background:'linear-gradient(135deg,rgba(245,158,11,0.12),rgba(239,68,68,0.08))',borderBottom:'1px solid rgba(245,158,11,0.15)',display:'flex',alignItems:'center',gap:'10px' }}>
                  <motion.div animate={{ rotate:[0,-8,8,-8,8,0] }} transition={{ duration:0.6,repeat:Infinity,repeatDelay:2.5 }} style={{ width:'36px',height:'36px',borderRadius:'11px',flexShrink:0,background:'linear-gradient(135deg,#f59e0b,#ef4444)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'17px' }}>⚠️</motion.div>
                  <div style={{ flex:1 }}>
                    <p style={{ margin:0,fontWeight:800,fontSize:'14px',color:darkMode?'rgba(255,255,255,0.92)':'#0f172a' }}>{t('pending_items_title')}</p>
                    <motion.p animate={{ opacity:[0.6,1,0.6] }} transition={{ duration:2,repeat:Infinity }} style={{ margin:0,fontSize:'11px',fontWeight:700,color:'#d97706',fontFamily:'monospace' }}>{pendingReminders.length} {t('missing_checks')}</motion.p>
                  </div>
                  <motion.button onClick={()=>setPendingReminders([])} whileHover={{ scale:1.1,rotate:90 }} whileTap={{ scale:0.9 }} style={{ width:'26px',height:'26px',borderRadius:'8px',border:'none',cursor:'pointer',background:'rgba(0,0,0,0.05)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </motion.button>
                </div>
                <div style={{ padding:'12px 14px' }}>
                  <div style={{ display:'flex',flexDirection:'column',gap:'5px',marginBottom:'12px',maxHeight:'150px',overflowY:'auto' }}>
                    {pendingReminders.slice(0,4).map((item,idx) => (
                      <div key={`${item.section}-${item.item}`} style={{ display:'flex',alignItems:'flex-start',gap:'8px',padding:'7px 10px',borderRadius:'10px',background:'rgba(254,243,199,0.6)',border:'1px solid rgba(245,158,11,0.2)' }}>
                        <span style={{ width:'18px',height:'18px',borderRadius:'6px',background:'linear-gradient(135deg,#f59e0b,#ef4444)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'9px',fontWeight:800,color:'white',flexShrink:0 }}>{idx+1}</span>
                        <span style={{ fontSize:'12.5px',fontWeight:500,color:'#78350f' }}>{item.text}</span>
                      </div>
                    ))}
                    {pendingReminders.length>4 && <p style={{ margin:0,fontSize:'11px',color:'#94a3b8',textAlign:'center',fontStyle:'italic' }}>{t('more_items', { count: pendingReminders.length - 4 })}</p>}
                  </div>
                  <div style={{ display:'flex',gap:'8px' }}>
                    <motion.button onClick={startReviewSkipped} whileHover={{ scale:1.03,y:-1 }} whileTap={{ scale:0.96 }} style={{ flex:1,padding:'11px',background:'linear-gradient(135deg,#f59e0b,#ef4444)',color:'white',border:'none',borderRadius:'12px',fontWeight:700,fontSize:'12.5px',cursor:'pointer' }}>{t('review')}</motion.button>
                    <motion.button onClick={()=>setPendingReminders([])} whileTap={{ scale:0.96 }} style={{ flex:1,padding:'11px',background:'rgba(0,0,0,0.04)',color:'#64748b',border:'1px solid rgba(0,0,0,0.08)',borderRadius:'12px',fontWeight:600,fontSize:'12.5px',cursor:'pointer' }}>{t('dismiss')}</motion.button>
                  </div>
                  <p style={{ margin:'10px 0 0',fontSize:'10.5px',fontWeight:600,color:'rgba(217,119,6,0.7)',textAlign:'center',fontFamily:'monospace' }}>{t('incomplete_check_warning')}</p>
                </div>
              </motion.div>
            </div>
          )}

          <WeatherInfo darkMode={darkMode} onWeatherUpdate={setWeatherData} />
          <PreflightLog sessionData={sessionData} onUpdateSession={handleUpdateSession} darkMode={darkMode} />

          {selectedAircraft && (
            <>
              <VoiceAssistant
                currentItem={currentItemText} isChecking={isChecking}
                onVoiceConfirm={handleVoiceConfirm} voiceEnabled={voiceEnabled}
                onToggleVoice={handleToggleVoice} justConfirmed={justConfirmed}
                firstCheckDone={firstCheckDone} darkMode={darkMode}
                onToggleDarkMode={handleToggleDarkMode}
                onShowVoiceSelector={()=>setShowVoiceSelector(true)}
                isSpeaking={isSpeaking}
              />
              <ChecklistDisplay
                checklist={currentChecklist} checkedItems={checkedItems} skippedItems={skippedItems}
                pendingReminders={pendingReminders} currentSectionIndex={currentSectionIndex}
                currentItemIndex={currentItemIndex} pendingItem={pendingItem}
                onItemInteraction={handleItemInteraction} isSectionComplete={isSectionComplete}
                darkMode={darkMode} reviewingSkipped={reviewingSkipped}
                itemPhotos={itemPhotos} onAddPhoto={handleAddPhoto} onRemovePhoto={handleRemovePhoto}
                onShowReference={openRefModal}
                observationItems={observationItems}
                onObservationRequest={handleObservationRequest}
                onHobbsConfirm={handleHobbsRecord}
                onOilConfirm={handleOilRecord}
                onFuelConfirm={handleFuelRecord}
                aircraft={selectedAircraft}
              />
            </>
          )}
        </motion.div>
      </div>

      {showEditor && selectedAircraft && <ChecklistEditor aircraft={selectedAircraft} onSave={handleSaveCustomChecklist} onCancel={()=>setShowEditor(false)} darkMode={darkMode} />}

      <AnimatePresence>
        {referenceModal && <ReferenceImageModal item={referenceModal} onClose={closeRefModal} darkMode={darkMode} autoDismiss={false} />}
      </AnimatePresence>
      <AnimatePresence>
        {showVoiceSelector && <VoiceSelector aircraft={selectedAircraft} darkMode={darkMode} onClose={()=>setShowVoiceSelector(false)} orgFeatures={currentUser?.organization_features || {}} />}
      </AnimatePresence>
      <AnimatePresence>
        {showReviewModal && skippedReviewQueue.length>0 && (
          <ReviewModal isOpen={showReviewModal} pendingItems={skippedReviewQueue} currentIndex={reviewIndex} aircraft={selectedAircraft} onReviewItem={handleReviewItemInModal}
            onClose={()=>{ stopSpeech(); setShowReviewModal(false); setReviewIndex(0); setVoiceEnabled(true); }} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showOnboarding && <OnboardingSlides key="onboarding" onComplete={handleOnboardingComplete} darkMode={darkMode} />}
      </AnimatePresence>
      <AnimatePresence>
        {showHistory && <PilotHistory darkMode={darkMode} onClose={()=>setShowHistory(false)} />}
      </AnimatePresence>
      <AnimatePresence>
        {pendingObservation && (
          <ObservationModal item={pendingObservation.itemObj} darkMode={darkMode} onConfirm={handleObservationConfirm} onCancel={() => setPendingObservation(null)} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showSessionWarning && <SessionWarningModal darkMode={darkMode} onRenew={handleRenewSession} onLogout={handleLogout} />}
        {demoBlockedMessage !== null && <DemoBlockedModal darkMode={darkMode} message={demoBlockedMessage} onClose={() => setDemoBlockedMessage(null)} />}
      </AnimatePresence>
      <TourGuide active={showTour} onDone={() => setShowTour(false)} darkMode={darkMode} />
    </div>
  );
};

export default App;
